import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { View, StyleSheet, Image, Platform, Alert, Animated, KeyboardAvoidingView, FlatList, ScrollView, TextInput, Keyboard } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import { GiftedChat } from 'react-native-gifted-chat';
import { DefaultRootState } from '@reduxLocal/persistState';
import { Text, Card } from '@constants/Themed';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { LinearGradient } from 'expo-linear-gradient';
import { selectUser, selectUserAvatar, selectUserProfile } from '@reduxLocal/selectors/userSelectors';
import useGlobalUser from '@hooks/getGlobalUser';
import { convertGiftedChatToHistory } from '@components/modals/HelpModal/utils/handleHelp';
import handleAiHelp from '@components/modals/HelpModal/utils/handleHelp';
import { useEditorContext } from '@hooks/toolbarContext';
import MyInputToolbar from '@navigation/screens/MidLevel/MessageScreen/Components/InputToolbar';
import AnimatedLottieView from '@utils/lottieAnimation';
import { AntDesign, FontAwesome, MaterialCommunityIcons, MaterialIcons } from '@expo/vector-icons';
import { Pressable, TouchableOpacity } from 'react-native';
import { useVoiceTyping } from '@hooks/useVoiceTyping';
import { TOGGLE_EASY_MODE, UPDATE_USER, DISMISS_EASY_MODE_SAVE_PROMPT } from '@reduxLocal/actions/types';
import * as Animatable from 'react-native-animatable';
import { uniqueId, debounce, values } from 'lodash';
import { AI_MODEL, HOST } from '@api/constant';
import ActionArray from "@components/UI/ContextActions.tsx/ActionArray";
import { XStack, YStack, H4, Paragraph, Text as TamaguiText, Button as TamaguiButton } from "tamagui";
import {
  analyzeResponse,
  isMapperReady,
  getRegisteredTemplates
} from '@utils/semanticActionMapper';
import { ActionChips, ActionDataForm, PostExecutionFeedback } from '@components/ActionConfirmation';
import type { ActionTemplate, MatchedAction } from '@utils/semanticActionMapper';
import { isSemanticActionMapperReady } from '@utils/semanticActionMapperSingleton';
import { useNavigation, useRoute } from '@react-navigation/native';
import { extractDataHybrid } from '@utils/nlpDataExtractor';
import { getSchemaForAction } from '@utils/schemaCache';
import { submitFeedback, preloadFeedbackData } from '@utils/actionFeedbackManager';
import { useAlertDialog } from '@components/Global/AlertDialog/useAlertDialog';
import { saveChat } from '@api/api';
import ActionHierarchy, { ActionHierarchyHandle } from '@components/Hierarchy/ActionHierarchy';
import { parseAutomationFlowCommands, AutomationFlowCommand } from '@utils/automationFlowCommands';
import { mapScreenToBadgeKey } from '@utils/pageBadgeMapper';
import { UPDATE_PAGE_BADGES } from '@reduxLocal/actions/types';
import SafeMarkDown from './Components/SafeMarkDown';
import ActionCardMessage from './Components/ActionCardMessage';
import FormMessage, { FormMessageHandle } from './Components/FormMessage';
import QuestionMessage from './Components/QuestionMessage';
import ParsedTextWithReferences from './Components/ParsedTextWithReferences';
import { DataSourceSelector } from './Components/DataSourceSelector';
import DashboardBuilder from './Components/DashboardBuilder';
import { parsePlanQuestions, formatAnswersForSubmission, type PlanQuestion } from '@utils/planQuestionParser';
import UnifiedWindow, { WindowComponentType } from './Components/UnifiedWindow';
import { Loading } from '@components/Global/Loading';
import ImageCanvas from '@components/Canvas/ImageCanvas';
import type { ImageCanvasHandle } from '@components/Canvas/types';
import CanvasToolbar from '@components/Canvas/CanvasToolbar';
import MediaSelector from '@components/Canvas/MediaSelector';
import { CanvasLayer } from '@components/Canvas/types';
import DataHandler from '@navigation/screens/MidLevel/DataScreen/DataHandler';
import DataHandlerHeader from '@navigation/NavigationStacks/Components/DataHandlerHeader';
import parseSchema from '@navigation/NavigationStacks/utils/parseSchema';
import {
  OPEN_IMAGE_CANVAS,
  ENHANCE_IMAGE,
  REMOVE_BACKGROUND,
  STYLIZE_IMAGE,
  GENERATE_IMAGE,
} from '@utils/canvasActionTemplates';
import {
  OPEN_RICH_TEXT_EDITOR,
} from '@utils/editorActionTemplates';
import DomEditor, { DomEditorHandle } from '@navigation/screens/LowLevel/Components/DomEditor';
import apis from '@api/api';
import { getFileType, detectDelimiter, detectDataType, processDataFile, processMediaFile, uploadMediaFile, handleDroppedFile, handleDrop, handleMetadataSubmit } from '@utils/fileProcessingHelpers';
import onboardSchema from '@components/modals/TutorialModal/utils/onboardSchema';
import profileSchema from '@components/modals/TutorialModal/utils/profileSchema';
import { START_ONBOARDING, COMPLETE_ONBOARDING, SKIP_ONBOARDING, SET_TUTORIAL_COMPLETE, RESTORE_CHAT_HISTORY, SET_RECOMMENDED_INTEGRATIONS, HIDE_LOADING_MODAL, SHOW_LOADING_MODAL, SHOW_AUTH_MODAL } from '@reduxLocal/actions/types';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { detectAICreationIntent, hasHighConfidence } from '@utils/aiIntentDetector';
// Utility imports
import { parseInlineDataRefs, getChatFingerprint, hasChatToSave, isSavePromptDismissed, addDisclaimer } from './utils/chatUtils';
import { createHandleDroppedFile, createHandleDrop, createHandleDragOver, createHandleDragLeave } from './utils/fileHandlers';
import { handleProfileFormSubmit, handleBusinessFormSubmit } from './utils/onboardingHandlers';
import { handleModelSelection, handleModelSelectionSkip } from './utils/modelHandlers';
import { createHandleExecuteAction, createHandleActionCardClick } from './utils/actionHandlers';
import { createHandleCanvasLayerUpdate, createHandleCanvasLayerAdd, createHandleCanvasLayerRemove, createHandleMediaSelect, createHandleCanvasAIEnhance, createHandleCanvasAIGenerate, createHandleCanvasSaveToMedia } from './utils/canvasHandlers';
import { createHandleReportsKeyword, createHandleDataSourceSelection } from './utils/reportsHandlers';
import { createFetchIntegrationSuggestions } from './utils/integrationHandlers';
import { calculateWindowWidth, createHandleToggleWindow, createWindowToggleHandlers } from './utils/windowHandlers';
import { createHandleSendToEditor, createHandleSendSummaryToEditor, createHandleSendConversationToEditor } from './utils/editorHandlers';
import { createSaveChatConversation } from './utils/saveChatUtils';
import { createHandleSheetSelection } from './utils/sheetHandlers';
import { addDisclaimer as addDisclaimerUtil } from './utils/chatUtils';
import { formatActionDescriptionForEasyMode } from '@utils/easyModeActionFormatter';
import InputWrapper from './Components/utils/InputWrapper';
import { SheetSelectorModal } from './Components/SheetSelectorModal';
import { BackgroundEffects } from './Components/BackgroundEffects';
import { useEasyModeState } from './hooks/useEasyModeState';
import { useEasyModeWindow } from './hooks/useEasyModeWindow';
import { useEasyModeCanvas } from './hooks/useEasyModeCanvas';
import { useEasyModeOnboarding } from './hooks/useEasyModeOnboarding';
import { useEasyModeBackground } from './hooks/useEasyModeBackground';
import { useEasyModeChat } from './hooks/useEasyModeChat';
import {
  createHandleCreateDocument,
  createHandleDocumentLayoutApproval,
  createHandleDocumentContentApproval,
  createHandleDocumentDesignChange,
} from './utils/documentHandlers';
import type { Document, DocumentElement } from './Components/DocumentWindow/types';
import AgentBuilderModal, { AgentBuilderModalHandle } from '@navigation/screens/MidLevel/UsersScreen/Components/AgentBuilderModal';
import { useReactiveWindowDimensions } from '@hooks/useReactiveWindowDimensions';




// Helper function to get agent avatar image source
const getAgentAvatarSource = (agent: any) => {
  // Map of avatar filenames to require statements
  const avatarMap: Record<string, any> = {
    'Assistant.png': require('@assets/images/1x/Assistant.png'),
    'BusinessDeveloper.png': require('@assets/images/1x/BusinessDeveloper.png'),
    'SalesMarketer.png': require('@assets/images/1x/SalesMarketer.png'),
    'Researcher.png': require('@assets/images/1x/Researcher.png'),
  };

  // Map of agent types to require statements (fallback)
  const typeMap: Record<string, any> = {
    'assistant': require('@assets/images/1x/Assistant.png'),
    'business_developer': require('@assets/images/1x/BusinessDeveloper.png'),
    'sales_marketer': require('@assets/images/1x/SalesMarketer.png'),
    'researcher': require('@assets/images/1x/Researcher.png'),
  };

  // First try to use the avatar field if it exists
  if (agent.avatar && avatarMap[agent.avatar]) {
    return avatarMap[agent.avatar];
  }

  // Fallback to agent type
  if (agent.type && typeMap[agent.type]) {
    return typeMap[agent.type];
  }

  // Default to Assistant if nothing matches
  return require('@assets/images/1x/Assistant.png');
};

const EasyModeScreen = () => {
  const theme = themeSelector();
  const { height: screenHeight, width: screenWidth } = useReactiveWindowDimensions();
  const globalUser = useGlobalUser();
  const userAvatar = useSelector(selectUserAvatar);
  const userProfile = selectUser();
  const profileInformation = userProfile || {};
  const userData = useSelector((state: DefaultRootState) => state.promiseData.data.userData);
  const dispatch = useDispatch();
  const editorContextHook = useEditorContext();
  const { isEditorConnected, editorContext } = editorContextHook;
  const navigation = useNavigation();
  const route = useRoute();
  const { showConfirm, showAlert } = useAlertDialog();
  const queryClient = useQueryClient();
  const reduxActions = useSelector((state: DefaultRootState) => state.actionMaker.present.actions) || [];
  // now use canvas.canvasRef everywhere

  console.log("remounted easy mode screen")

  const window = useEasyModeWindow();
  const {
    activeWindowComponent,
    setActiveWindowComponent,
    windowLoading,
    setWindowLoading,
    windowWidthAnim,
    windowWidth,
    showHierarchy,
    showCanvas,
    showEditor,
    showDataHandler,
    showDocument,
    hierarchyRef,
    hasHierarchyActions,
    setHasHierarchyActions,
    handleToggleWindow,
    handleToggleHierarchy,
    handleToggleCanvas,
    handleToggleEditor,
    handleToggleDataHandler,
  } = window;

  // Combined data file processed callback for both explicit upload and drag/drop
  const handleDataFileProcessed = useCallback(() => {
    setActiveWindowComponent('dataHandler');
    // Optionally, navigate to DataHandler if not already there
    // if (navigationRef.isReady()) {
    //   (navigationRef as any).navigate('DataHandler');
    // }
  }, [setActiveWindowComponent]);

  const state = useEasyModeState();
  const {
    // Chat state
    chatMessages, setChatMessages,
    inputDisabled, setInputDisabled,
    mode, setMode,
    suggestedActions, setSuggestedActions,
    // Refs
    inputRef,
    isProcessingRef,
    timeoutIdsRef,
    isClosingRef,
    chatFingerprintRef,
    scrollViewRef,
    inputToolbarTextRef,
    // Semantic Action Mapper state
    pendingActions, setPendingActions,
    actionRequiringData, setActionRequiringData,
    isMapperInitialized, setIsMapperInitialized,
    extractedFormData, setExtractedFormData,
    currentUserQuery, setCurrentUserQuery,
    showPostExecutionFeedback, setShowPostExecutionFeedback,
    lastExecutedAction, setLastExecutedAction,
    // Report Dashboard State
    selectedDataSources, setSelectedDataSources,
    reportDashboardConfig, setReportDashboardConfig,
    pendingReportData, setPendingReportData,
    // Drag and Drop State
    draggedOver, setDraggedOver,
    metadataDialogVisible, setMetadataDialogVisible,
    pendingMediaFile, setPendingMediaFile,
    mediaMetadata, setMediaMetadata,
    // Image Analysis State
    pendingImageAnalysis, setPendingImageAnalysis,
    // Excel Multi-Sheet Selection State
    availableSheets, setAvailableSheets,
    showSheetSelector, setShowSheetSelector,
    pendingWorkbook, setPendingWorkbook,
    pendingFileName, setPendingFileName,
    // Data models state
    userDataForms, setUserDataForms,
    selectedDataModel, setSelectedDataModel,
    // Additional refs
    dismissedPromptsLoadedRef,
    routeParamsProcessedRef,
    hierarchyRefetchAttemptedRef,
  } = state;
  const [showMediaSelector, setShowMediaSelector] = useState(false);
  const [selectedCanvasLayerId, setSelectedCanvasLayerId] = useState<string | null>(null);
  const [canvasLayers, setCanvasLayers] = useState<CanvasLayer[]>([]);
  // Agent selection state
  const [selectedAgent, setSelectedAgent] = useState<any>(null);

  // Debug: Log when selectedAgent changes
  useEffect(() => {
    console.log('Selected agent changed:', selectedAgent?.name || 'null');
  }, [selectedAgent]);

  // Document state
  const [document, setDocument] = useState<any>(null);
  const [currentDocumentPage, setCurrentDocumentPage] = useState(0);
  const [selectedDocumentElementId, setSelectedDocumentElementId] = useState<string | null>(null);
  const [documentCreationStage, setDocumentCreationStage] = useState<'idle' | 'layout_planning' | 'layout_approval' | 'content_generation' | 'content_approval' | 'design_refinement' | 'ready'>('idle');
  const [pendingLayout, setPendingLayout] = useState<any>(null);
  const [pendingContent, setPendingContent] = useState<any>(null);
  const canvas = useEasyModeCanvas(windowWidth, setInputDisabled);
  const agentBuilderModalRef = useRef<AgentBuilderModalHandle>(null);
  const preventScrollOnKeyboardRef = useRef<boolean>(false); // Prevent ScrollView from scrolling when keyboard appears
  const activeFormMessageRef = useRef<FormMessageHandle | null>(null);

  // Refs for callback functions to prevent renderInputToolbar from remounting
  // These refs allow us to use stable references in useCallback dependencies
  const handleQueryRef = useRef<((query: string) => Promise<void>) | undefined>(undefined);
  const handleModelPressRef = useRef<(() => void) | undefined>(undefined);
  const handleManualEditRef = useRef<((text: string) => void) | undefined>(undefined);
  const handleDragOverRef = useRef<((e: any) => void) | undefined>(undefined);
  const handleDragLeaveRef = useRef<((e?: any) => void) | undefined>(undefined);
  const handleDropRef = useRef<((e: any) => void) | undefined>(undefined);
  const handleDataFileProcessedRef = useRef<(() => void) | undefined>(undefined);
  const handleToggleHierarchyRef = useRef<(() => void) | undefined>(undefined);
  const handleToggleCanvasRef = useRef<(() => void) | undefined>(undefined);
  const handleToggleEditorRef = useRef<(() => void) | undefined>(undefined);
  const handleToggleDataHandlerRef = useRef<(() => void) | undefined>(undefined);
  const fetchIntegrationSuggestionsRef = useRef<(() => void) | undefined>(undefined);

  // Memoize hasMessages to prevent recalculation on every render
  const hasMessages = useMemo(() => {
    return chatMessages.some(msg =>
      (msg.text &&
        typeof msg.text === 'string' &&
        msg.text.trim().length > 0 &&
        !msg.system) || // Regular user/AI messages
      (msg.system && (
        // System messages with interactive content
        msg.showProfileForm || msg.showOnboardingForm || msg.formSchema || msg.actionCards ||
        msg.showDataSourceSelector || msg.showDashboardBuilder || msg.planQuestions ||
        // System messages with text content (e.g., skip messages, informational messages)
        (msg.text && typeof msg.text === 'string' && msg.text.trim().length > 0)
      ))
    );
  }, [chatMessages]);

  // Detect active form message (has formSchema and not yet submitted)
  const activeFormMessage = useMemo(() => {
    return chatMessages.find(msg =>
      msg.system &&
      msg.formSchema &&
      !msg.formSubmitted
    );
  }, [chatMessages]);

  // Fetch agents (active and draft)
  const { data: agentsData } = useQuery({
    queryKey: ['activeAgents'],
    queryFn: async () => {
      try {
        const response = await apis.get('/agents');
        const agents = response?.data?.data || response?.data || [];
        // Filter to show active and draft agents (exclude inactive)
        return agents.filter((agent: any) => agent.status === 'active' || agent.status === 'draft');
      } catch (error) {
        console.error('Error fetching agents:', error);
        return [];
      }
    },
    staleTime: 30000, // Cache for 30 seconds
  });

  const activeAgents = agentsData || [];

  const {
    isRecording,
    voiceInputText,
    handleVoiceButtonPress,
    clearVoiceInput,
    handleManualEdit,
  } = useVoiceTyping({
    getCurrentText: () => inputToolbarTextRef.current || '',
    onTextUpdate: (text) => {
      inputToolbarTextRef.current = text;
    },
    inputDisabled: state.inputDisabled, // Link input disabled state to voice input
  });

  // Check if hierarchy has actions
  useEffect(() => {
    if (hierarchyRef.current) {
      const aiActions = hierarchyRef.current.getAIManagedActions() || [];
      const hasActions = aiActions.length > 0 || reduxActions.length > 0;
      setHasHierarchyActions(hasActions);
    } else {
      setHasHierarchyActions(reduxActions.length > 0);
    }
  }, [reduxActions, activeWindowComponent]);

  // Prevent GiftedChat ScrollView from scrolling when keyboard appears (causes focus loss)
  useEffect(() => {
    console.log('🔧 [EasyModeScreen] Setting up keyboard listeners - Platform:', Platform.OS);

    if (Platform.OS === 'web') {
      console.log('✅ [EasyModeScreen] Setting up web keyboard detection');

      // On web, use visualViewport API to detect keyboard appearance
      if (typeof window !== 'undefined') {
        const win = typeof window !== 'undefined' ? (window as any as Window) : null;
        if (!win || typeof win.addEventListener !== 'function') {
          console.warn('⚠️ [EasyModeScreen] Window API not available');
          return;
        }

        const layoutViewportHeight = win.innerHeight;
        let lastViewportHeight = layoutViewportHeight;
        let keyboardDetectionTimeout: NodeJS.Timeout | null = null;

        const handleKeyboardAppearance = () => {
          console.log('🔑 [EasyModeScreen] Web keyboard detected - preventing scroll');
          preventScrollOnKeyboardRef.current = true;

          // Immediately restore focus if input exists
          if (inputRef.current) {
            console.log('🔑 [EasyModeScreen] Web keyboard appeared - restoring focus immediately');
            // Multiple immediate attempts
            inputRef.current.focus();
            setTimeout(() => {
              inputRef.current?.focus();
              console.log('🔑 [EasyModeScreen] Web focus attempt 1 (0ms)');
            }, 0);
            setTimeout(() => {
              inputRef.current?.focus();
              console.log('🔑 [EasyModeScreen] Web focus attempt 2 (10ms)');
            }, 10);
            setTimeout(() => {
              inputRef.current?.focus();
              console.log('🔑 [EasyModeScreen] Web focus attempt 3 (50ms)');
            }, 50);
          }

          // Re-enable scrolling after delay
          if (keyboardDetectionTimeout) {
            clearTimeout(keyboardDetectionTimeout);
          }
          keyboardDetectionTimeout = setTimeout(() => {
            console.log('🔑 [EasyModeScreen] Re-enabling scroll after 1000ms');
            preventScrollOnKeyboardRef.current = false;
          }, 1000);
        };

        // Try visualViewport API first (more accurate)
        const visualViewport = (win as any).visualViewport;
        if (visualViewport && typeof visualViewport.addEventListener === 'function') {
          console.log('✅ [EasyModeScreen] Using visualViewport API for keyboard detection');

          const handleViewportResize = () => {
            const currentHeight = visualViewport.height;
            const heightDiff = layoutViewportHeight - currentHeight;

            // If viewport height decreased significantly (>150px), keyboard likely appeared
            if (heightDiff > 150 && currentHeight < lastViewportHeight) {
              handleKeyboardAppearance();
            } else if (heightDiff < 50) {
              // Keyboard likely dismissed
              console.log('🔑 [EasyModeScreen] Web keyboard dismissed');
              preventScrollOnKeyboardRef.current = false;
            }

            lastViewportHeight = currentHeight;
          };

          visualViewport.addEventListener('resize', handleViewportResize);

          return () => {
            if (visualViewport && typeof visualViewport.removeEventListener === 'function') {
              visualViewport.removeEventListener('resize', handleViewportResize);
            }
            if (keyboardDetectionTimeout) {
              clearTimeout(keyboardDetectionTimeout);
            }
          };
        } else {
          // Fallback: detect keyboard via window resize (less reliable)
          console.log('⚠️ [EasyModeScreen] visualViewport not available, using window resize fallback');
          let lastHeight = win.innerHeight;

          const handleResize = () => {
            const currentHeight = win.innerHeight;
            const heightDiff = lastHeight - currentHeight;

            // If height decreased significantly, keyboard might have appeared
            if (heightDiff > 150) {
              handleKeyboardAppearance();
            }

            lastHeight = currentHeight;
          };

          win.addEventListener('resize', handleResize);

          return () => {
            win.removeEventListener('resize', handleResize);
            if (keyboardDetectionTimeout) {
              clearTimeout(keyboardDetectionTimeout);
            }
          };
        }
      }
      return;
    }

    // Native platform - use Keyboard API
    console.log('✅ [EasyModeScreen] Registering keyboard listeners for native platform');
    const showSubscription = Keyboard.addListener('keyboardDidShow', () => {
      console.log('🔑 [EasyModeScreen] Keyboard appeared - preventing ScrollView scroll');
      // Prevent ScrollView from scrolling when keyboard appears
      preventScrollOnKeyboardRef.current = true;
      console.log('🔑 [EasyModeScreen] preventScrollOnKeyboardRef set to true');
      // Immediately try to restore focus if input exists
      if (inputRef.current) {
        console.log('🔑 [EasyModeScreen] Input ref exists, attempting to restore focus');
        // Multiple immediate attempts to restore focus
        setTimeout(() => {
          console.log('🔑 [EasyModeScreen] Focus attempt 1 (0ms)');
          inputRef.current?.focus();
        }, 0);
        setTimeout(() => {
          console.log('🔑 [EasyModeScreen] Focus attempt 2 (10ms)');
          inputRef.current?.focus();
        }, 10);
        setTimeout(() => {
          console.log('🔑 [EasyModeScreen] Focus attempt 3 (50ms)');
          inputRef.current?.focus();
        }, 50);
      } else {
        console.warn('⚠️ [EasyModeScreen] Input ref is null when keyboard appeared');
      }
      // Re-enable scrolling after a longer delay to ensure keyboard stays open
      setTimeout(() => {
        console.log('🔑 [EasyModeScreen] Re-enabling scroll after 1000ms');
        preventScrollOnKeyboardRef.current = false;
      }, 1000); // Increased to 1000ms to ensure keyboard stays open
    });

    const hideSubscription = Keyboard.addListener('keyboardDidHide', () => {
      console.log('🔑 [EasyModeScreen] Keyboard hidden - re-enabling ScrollView scroll');
      preventScrollOnKeyboardRef.current = false;
    });

    return () => {
      console.log('🔧 [EasyModeScreen] Cleaning up keyboard listeners');
      showSubscription.remove();
      hideSubscription.remove();
    };
  }, [inputRef]);

  // Animate unified window in/out with lazy loading
  useEffect(() => {
    if (activeWindowComponent) {
      const targetWidth = windowWidth;
      // Start loading when window opens
      setWindowLoading(true);
      // Simulate component loading (replace with actual lazy load)
      setTimeout(() => {
        setWindowLoading(false);
      }, 300);
      Animated.timing(windowWidthAnim, {
        toValue: targetWidth,
        duration: 300,
        useNativeDriver: false, // width requires layout animation
      }).start();
    } else {
      Animated.timing(windowWidthAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: false,
      }).start();
      setWindowLoading(false);
    }
  }, [activeWindowComponent, windowWidth, windowWidthAnim, setWindowLoading]);

  // Redux state selectors
  const easyMode = useSelector((state: DefaultRootState) => state.UI.easyMode);
  const focusMode = useSelector((state: DefaultRootState) => state.UI.focusMode);
  const chatRestoration = useSelector((state: DefaultRootState) => state.modals.helpModal.chatRestoration);

  // Ensure easyMode is set to true when EasyModeScreen is mounted/focused
  useFocusEffect(
    useCallback(() => {
      // Only set easyMode to true if it's currently false
      // This ensures chat history is shown in LazyMessages
      if (!easyMode) {
        dispatch({ type: TOGGLE_EASY_MODE });
      }
      return () => {
        // Optional: You might want to keep easyMode true even when screen loses focus
        // Or toggle it off when leaving - but that might not be desired
      };
    }, [easyMode, dispatch])
  );
  // Direct selector - no transformation needed, so use useSelector directly
  const dismissedSavePrompts = useSelector((state: DefaultRootState) => state.modals.helpModal.dismissedSavePrompts);
  const onboardingInProgress = useSelector((state: DefaultRootState) => state.UI.onboardingInProgress);

  // Debounced function to save dismissed prompts to backend
  const saveDismissedPromptsToBackend = useCallback(
    debounce(async (prompts: string[]) => {
      try {
        const result = await apis.saveDismissedSavePrompts(prompts);
        if (result?.success) {
        } else {
          const errorMsg = (result as any)?.error || 'Unknown error';
          console.warn('⚠️ [EasyModeScreen] Failed to save dismissed prompts to backend:', errorMsg);
        }
      } catch (error) {
        console.error('❌ [EasyModeScreen] Error saving dismissed prompts to backend:', error);
        // Don't throw - this is a background operation, failure shouldn't block UI
      }
    }, 1000), // Debounce by 1 second to batch multiple dismissals
    []
  );



  // Track if we've processed route params to avoid re-processing

  // Initialize chat messages from route params (when navigating from HelpModal)
  // Use useFocusEffect to handle route params when screen comes into focus
  useFocusEffect(
    useCallback(() => {
      const params = route.params as { chatMessages?: any[]; chatId?: string; chatTitle?: string } | undefined;

      if (params?.chatMessages && params.chatMessages.length > 0) {
        // Create a unique key from the first and last message IDs to track if we've processed these messages
        const messagesKey = `${params.chatMessages[0]?._id || ''}-${params.chatMessages[params.chatMessages.length - 1]?._id || ''}`;

        // Skip if we've already processed these exact messages
        if (routeParamsProcessedRef.current === messagesKey) {
          return;
        }


        // Deserialize chat messages (convert ISO strings back to Date objects)
        const deserializedMessages = params.chatMessages.map(msg => ({
          ...msg,
          createdAt: typeof msg.createdAt === 'string'
            ? new Date(msg.createdAt)
            : (msg.createdAt instanceof Date ? msg.createdAt : new Date())
        }));

        // Restore the chat messages
        setChatMessages(deserializedMessages);

        // Mark as processed
        routeParamsProcessedRef.current = messagesKey;

        // Dispatch RESTORE_CHAT_HISTORY to update Redux state (similar to HelpModal)
        // This ensures the chat history list knows about the active chat
        dispatch({
          type: RESTORE_CHAT_HISTORY,
          payload: {
            messages: deserializedMessages,
            chatId: params.chatId,
            chatTitle: params.chatTitle || 'Untitled',
            target: 'easyMode'
          }
        });
      } else {
        // Reset processed ref when no params (allows new params to be processed)
        routeParamsProcessedRef.current = null;
      }

      // Cleanup: Reset ref when screen loses focus to allow re-processing on next focus
      return () => {
        routeParamsProcessedRef.current = null;
      };
    }, [route.params, dispatch])
  );

  // Load dismissed save prompts from userData when it's available
  useEffect(() => {
    if (dismissedPromptsLoadedRef.current || !globalUser || !userData) return;

    // Check if userData has dismissed prompts
    if (userData?.easyModeDismissedSavePrompts && Array.isArray(userData.easyModeDismissedSavePrompts)) {
      // Merge with existing Redux state
      const existingPrompts = dismissedSavePrompts || [];
      const newPrompts = userData.easyModeDismissedSavePrompts.filter(
        (fp: string) => !existingPrompts.includes(fp)
      );
      if (newPrompts.length > 0) {
        // Update Redux with merged list
        newPrompts.forEach((fingerprint: string) => {
          dispatch({ type: DISMISS_EASY_MODE_SAVE_PROMPT, payload: fingerprint });
        });
      }
      dismissedPromptsLoadedRef.current = true;
    }
  }, [userData, globalUser, dismissedSavePrompts, dispatch]);

  // Load dismissed save prompts from backend API if not in userData
  useEffect(() => {
    const loadDismissedPromptsFromBackend = async () => {
      // Skip if already loaded from userData or no globalUser
      if (dismissedPromptsLoadedRef.current || !globalUser) return;

      // Skip if userData exists but doesn't have the field (might be loading)
      if (userData && !userData.easyModeDismissedSavePrompts) {
        // Wait a bit for userData to fully load
        return;
      }

      try {
        // Load from backend API as fallback
        const backendPrompts = await apis.getDismissedSavePrompts();
        if (backendPrompts.length > 0) {
          // Merge with existing Redux state
          const existingPrompts = dismissedSavePrompts || [];
          backendPrompts.forEach((fingerprint: string) => {
            if (!existingPrompts.includes(fingerprint)) {
              dispatch({ type: DISMISS_EASY_MODE_SAVE_PROMPT, payload: fingerprint });
            }
          });
        }
        dismissedPromptsLoadedRef.current = true;
      } catch (error) {
        console.error('❌ [EasyModeScreen] Error loading dismissed prompts from backend:', error);
        dismissedPromptsLoadedRef.current = true; // Mark as loaded even on error to prevent retries
      }
    };

    // Delay backend load slightly to allow userData to load first
    const timeoutId = setTimeout(() => {
      loadDismissedPromptsFromBackend();
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [globalUser, userData, dismissedSavePrompts, dispatch]);

  // Fetch user's data forms on mount
  const fetchUserDataForms = useCallback(async () => {
    try {
      if (globalUser) {
        const userId = globalUser?._id || globalUser?.id || globalUser?.userId;
        const forms = await apis.getDataFolders({ userId: userId, dataFilter: 'all' });
        setUserDataForms(forms || []);
      }
    } catch (error) {
      console.error('❌ [EasyModeScreen] Error fetching user data forms:', error);
      setUserDataForms([]);
    }
  }, [globalUser]);

  useEffect(() => {
    fetchUserDataForms();
  }, [fetchUserDataForms]);

  // Refetch data forms once when hierarchy opens with no models
  useEffect(() => {
    if (activeWindowComponent === 'hierarchy' && userDataForms.length === 0 && !hierarchyRefetchAttemptedRef.current) {
      hierarchyRefetchAttemptedRef.current = true;
      fetchUserDataForms();
    }

    // Reset refetch flag when hierarchy closes
    if (activeWindowComponent !== 'hierarchy') {
      hierarchyRefetchAttemptedRef.current = false;
    }
  }, [activeWindowComponent, userDataForms.length, fetchUserDataForms]);

  // Check if semantic action mapper is ready
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;

    const checkMapperReady = () => {
      const isReady = isSemanticActionMapperReady();
      if (isReady && !isMapperInitialized) {
        setIsMapperInitialized(true);
      } else if (!isReady) {
        timeoutId = setTimeout(checkMapperReady, 500);
      }
    };

    checkMapperReady();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isMapperInitialized]);

  // Listen for chat restoration events (web)
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;

    const handleRestoreChat = (event: CustomEvent) => {
      // Only handle if easy mode is active
      if (!easyMode) return;

      const { messages, chatTitle, chatId } = event.detail;

      if (messages && messages.length > 0) {
        setChatMessages(messages);
      }
    };

    if (typeof window !== 'undefined' && typeof (window as any).addEventListener === 'function') {
      (window as any).addEventListener('restoreEasyModeChat', handleRestoreChat as EventListener);
    }
    return () => {
      if (typeof window !== 'undefined' && typeof (window as any).removeEventListener === 'function') {
        (window as any).removeEventListener('restoreEasyModeChat', handleRestoreChat as EventListener);
      }
    };
  }, [easyMode]);

  // Listen for chat restoration from Redux (native)
  useEffect(() => {
    if (Platform.OS === 'web') return; // Web uses CustomEvent above
    if (!easyMode) return; // Only handle if easy mode is active

    let timeoutId: NodeJS.Timeout | null = null;

    if (chatRestoration && chatRestoration.messages && chatRestoration.messages.length > 0) {
      // Check if this restoration is for easy mode
      if (chatRestoration.target === 'easyMode') {


        setChatMessages(chatRestoration.messages);

        timeoutId = setTimeout(() => {

        }, 100);

        // Clear the restoration data after processing
        dispatch({ type: 'RESTORE_CHAT_HISTORY', payload: null });
      }
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [chatRestoration, easyMode, dispatch]);

  // Handle onboarding flow - inject onboarding form when onboarding starts
  // IMPORTANT: Profile information must be collected BEFORE business details
  const onboardingFormInjectedRef = useRef(false);
  const previousOnboardingStateRef = useRef(onboardingInProgress);
  const [onboardingStage, setOnboardingStage] = useState<'profile' | 'context' | 'models' | null>(null);
  const [savedProfileData, setSavedProfileData] = useState<Record<string, any>>({});
  const [profileFormCompleted, setProfileFormCompleted] = useState(false);

  useEffect(() => {
    const wasOnboarding = previousOnboardingStateRef.current;
    const isOnboarding = onboardingInProgress;

    // When onboarding is skipped/completed (transition from true to false)
    if (wasOnboarding && !isOnboarding) {
      // Remove any existing onboarding forms from chat
      setChatMessages(prevMessages =>
        prevMessages.filter(msg => msg.showOnboardingForm !== true || msg.showProfileForm !== true)
      );
      // Reset the ref to allow future onboarding if needed
      onboardingFormInjectedRef.current = false;
      setOnboardingStage(null);
      setSavedProfileData({});
      setProfileFormCompleted(false);
    }

    // When onboarding starts (onboardingInProgress becomes true)
    if (isOnboarding && easyMode && !onboardingFormInjectedRef.current) {
      // Use functional update to check for existing form without adding to dependencies
      setChatMessages(prevMessages => {
        // Check if any onboarding form already exists in chat
        const hasProfileForm = prevMessages.some(msg => msg.showProfileForm === true);
        const hasBusinessForm = prevMessages.some(msg => msg.showOnboardingForm === true);

        // SAFEGUARD: Never inject business form before profile form is completed
        // If business form exists but profile form is not completed, remove business form
        if (hasBusinessForm && !profileFormCompleted && !hasProfileForm) {
          console.warn('⚠️ [EasyModeScreen] Business form detected before profile completion. Removing business form.');
          const cleanedMessages = prevMessages.filter(msg => msg.showOnboardingForm !== true);
          onboardingFormInjectedRef.current = false; // Reset to allow profile form injection
          return cleanedMessages;
        }

        // Only inject profile form if it doesn't exist and profile is not completed
        if (!hasProfileForm && !profileFormCompleted) {
          // Mark as injected immediately to prevent duplicate injections
          onboardingFormInjectedRef.current = true;
          setOnboardingStage('profile'); // Start with profile stage

          // Inject profile information form message into chat (Stage 1 - MUST BE FIRST)
          const profileMessageId = uniqueId();
          return GiftedChat.append(prevMessages, [{
            _id: profileMessageId,
            text: "Welcome to SmartSaaS! Let's start by collecting your basic profile information.",
            createdAt: new Date(),
            system: true,
            showProfileForm: true, // Flag for profile stage
            showOnboardingForm: false,
            formSchema: profileSchema,
            formSubmitted: false,
            formValues: {},
            received: true
          }]);
        }

        // Form already exists, mark as injected
        onboardingFormInjectedRef.current = true;
        //Scroll to the top of the chat container
        scrollViewRef.current?.scrollToOffset({ offset: 0, animated: true });
        return prevMessages;
      });
    }

    // Update previous state ref to track transitions
    previousOnboardingStateRef.current = isOnboarding;
  }, [onboardingInProgress, easyMode, profileFormCompleted]); // Added profileFormCompleted to dependencies

  // Use utility functions for chat fingerprint and save checks
  const getChatFingerprintMemo = useCallback(() => getChatFingerprint(chatMessages, globalUser), [chatMessages, globalUser]);
  const hasChatToSaveMemo = useCallback(() => hasChatToSave(chatMessages, globalUser), [chatMessages, globalUser]);
  const isSavePromptDismissedMemo = useCallback(() => isSavePromptDismissed(chatMessages, globalUser, dismissedSavePrompts), [chatMessages, globalUser, dismissedSavePrompts]);

  const handleFormSkip = useCallback(async (message: any) => {
    const isProfileForm = message?.showProfileForm === true;
    const isOnboardingForm = message?.showOnboardingForm === true;
    const currentFingerprint = getChatFingerprintMemo();


    // Dismiss the appropriate onboarding stage or simply close the form
    if (isProfileForm) {
      // If profile form is skipped, mark onboarding as complete
      dispatch({ type: COMPLETE_ONBOARDING });
      setOnboardingStage(null);
      setSavedProfileData({});
    } else if (isOnboardingForm) {
      // If business form is skipped, mark onboarding as complete
      dispatch({ type: COMPLETE_ONBOARDING });
      setOnboardingStage(null);
    }

    // Dismiss the form message from chat
    setChatMessages(prev => prev.filter(msg => msg._id !== message._id));

    // Also mark the current chat as dismissed for save prompts if it's not already
    if (currentFingerprint && !isSavePromptDismissedMemo()) {
      dispatch({ type: DISMISS_EASY_MODE_SAVE_PROMPT, payload: currentFingerprint });
      const updatedPrompts = [...(dismissedSavePrompts || []), currentFingerprint];
      saveDismissedPromptsToBackend(updatedPrompts);
    }

    // Add a system message acknowledging the skip
    sendMessageSequentially([
      {
        _id: uniqueId(),
        text: `You skipped the ${isProfileForm ? 'profile' : isOnboardingForm ? 'business context' : 'form'} setup. You can always complete it later.`,
        createdAt: new Date(),
        system: true,
        received: true
      }, {
        _id: uniqueId(),
        text: "Great! I have now unlocked the full capabilities of SmartSaaS for you. You can now explore all the features and start using the app.",
        createdAt: new Date(Date.now() + 1000),
        system: true,
        received: true
      }, {
        _id: uniqueId(),
        text: "Or simply ask me what you would like to do and I can help you with that.",
        createdAt: new Date(Date.now() + 2000),
        system: true,
        received: true
      }
    ]);

    //Scroll to the bottom of chat container
    scrollViewRef.current?.scrollToEnd({ animated: true });
  }, [dispatch, setOnboardingStage, setSavedProfileData, getChatFingerprint, isSavePromptDismissed, dismissedSavePrompts, saveDismissedPromptsToBackend, scrollViewRef]);

  const sendMessageSequentially = useCallback(async (messages: any[], delay = 1000) => {
    for (let i = 0; i < messages.length; i++) {
      await new Promise(resolve => setTimeout(resolve, delay));
      setChatMessages(prevMessages => GiftedChat.append(prevMessages, [messages[i]]));
    }
  }, [setChatMessages]);

  // Clear conversation history
  const clearConversationHistory = useCallback(() => {
    // History is extracted from chatMessages, so clearing chatMessages clears history
    console.log('🧹 Conversation history will be cleared when chatMessages are cleared');
  }, []);

  // Get dynamic input placeholder based on active window component
  const getInputPlaceholder = useCallback(() => {
    if (onboardingInProgress) {
      return "Let me help you onboard";
    }

    switch (activeWindowComponent) {
      case 'hierarchy':
        return "Describe your automation flow or workflow...";
      case 'canvas':
        // Check if there's a selected image layer for enhancement hints
        const hasSelectedImage = selectedCanvasLayerId && canvasLayers.find(l => l.id === selectedCanvasLayerId)?.type === 'image';
        if (hasSelectedImage) {
          return "Describe the image you want to create, enhance, upscale, or stylize...";
        }
        return "Describe the image you want to create or generate...";
      case 'editor':
        return "Type your content or ask me to generate text...";
      case 'dataHandler':
        return "Describe the data model or schema you want to create...";
      default:
        return "Ask me anything, upload a file or type / to select from your data...";
    }
  }, [onboardingInProgress, activeWindowComponent, selectedCanvasLayerId, canvasLayers]);

  const handleReportsKeyword = useCallback(
    createHandleReportsKeyword({
      dispatch,
      globalUser,
      setChatMessages,
      setSelectedDataSources,
      setReportDashboardConfig,
    }),
    [dispatch, globalUser, setChatMessages]
    // Note: setSelectedDataSources and setReportDashboardConfig are stable setter functions
    // from useState and don't need to be in the dependency array
  );

  // Ref to prevent multiple simultaneous calls to handleDataSourceSelection
  const isProcessingDataSourceSelectionRef = useRef(false);
  const handleDataSourceSelectionRef = useRef<((selectedIds: string[]) => Promise<void>) | null>(null);

  // Store stable references to avoid recreating handler
  // useState setters are stable, but we use refs to avoid dependency issues
  const setChatMessagesRef = useRef(setChatMessages);
  const setReportDashboardConfigRef = useRef(setReportDashboardConfig);
  const setSelectedDataSourcesRef = useRef(setSelectedDataSources);

  // Update refs on mount and when component updates (setters are stable, but refs ensure latest)
  useEffect(() => {
    setChatMessagesRef.current = setChatMessages;
    setReportDashboardConfigRef.current = setReportDashboardConfig;
    setSelectedDataSourcesRef.current = setSelectedDataSources;
  }); // No deps - run on every render to keep refs current, but this is cheap

  // Create the handler once and store it in a ref to prevent recreation
  useEffect(() => {
    handleDataSourceSelectionRef.current = async (selectedIds: string[]) => {
      // Prevent multiple simultaneous calls
      if (isProcessingDataSourceSelectionRef.current) {
        console.log('⚠️ [EasyModeScreen] Data source selection already processing, ignoring duplicate call');
        return;
      }

      // Early return if no IDs selected
      if (selectedIds.length === 0) {
        return;
      }

      try {
        isProcessingDataSourceSelectionRef.current = true;

        // Use the handler from the utility with refs to avoid dependency issues
        const handler = createHandleDataSourceSelection({
          dispatch,
          globalUser,
          setChatMessages: setChatMessagesRef.current,
          setSelectedDataSources: setSelectedDataSourcesRef.current,
          setReportDashboardConfig: setReportDashboardConfigRef.current,
        });

        await handler(selectedIds);
      } finally {
        isProcessingDataSourceSelectionRef.current = false;
      }
    };
  }, [dispatch, globalUser]); // Removed setChatMessages from deps - using ref instead

  // Handle data source selection confirmation - stable wrapper
  const handleDataSourceSelection = useCallback(
    async (selectedIds: string[]) => {
      if (handleDataSourceSelectionRef.current) {
        await handleDataSourceSelectionRef.current(selectedIds);
      }
    },
    [] // Empty deps - handler is stored in ref
  );

  // Fetch integration suggestions after onboarding
  const fetchIntegrationSuggestions = useCallback(
    createFetchIntegrationSuggestions({
      dispatch,
      globalUser,
      userData,
      queryClient,
      setChatMessages,
      setOnboardingStage,
      setSavedProfileData,
    }),
    [dispatch, globalUser, userData, queryClient, setChatMessages, setOnboardingStage, setSavedProfileData]
  );

  // Handle action execution (Ported from HelpSidePanel)

  // Handle sheet selection for multi-sheet Excel files
  const handleSheetSelection = useCallback(
    createHandleSheetSelection({
      dispatch,
      pendingWorkbook,
      pendingFileName,
      setShowSheetSelector,
      setAvailableSheets,
      setPendingWorkbook,
      setPendingFileName,
      handleDataFileProcessed,
    }),
    [dispatch, pendingWorkbook, pendingFileName, setShowSheetSelector, setAvailableSheets, setPendingWorkbook, setPendingFileName, handleDataFileProcessed]
  );

  // Handle dropped file - defined after helper functions it uses
  const handleDroppedFile = useCallback(
    createHandleDroppedFile({
      dispatch,
      setActiveWindowComponent,
      setAvailableSheets,
      setShowSheetSelector,
      setPendingWorkbook,
      setPendingFileName,
      setPendingMediaFile,
      setMetadataDialogVisible,
      setPendingImageAnalysis,
    }),
    [dispatch, setActiveWindowComponent, setAvailableSheets, setShowSheetSelector, setPendingWorkbook, setPendingFileName, setPendingMediaFile, setMetadataDialogVisible, setPendingImageAnalysis]
  );
  // handleDrop must be defined after handleDroppedFile
  const handleDrop = useCallback(
    createHandleDrop(draggedOver, setDraggedOver, handleDroppedFile),
    [draggedOver, setDraggedOver, handleDroppedFile]
  );

  // Handle drag over event
  const handleDragOver = useCallback(
    createHandleDragOver(setDraggedOver),
    [setDraggedOver]
  );

  // Handle drag leave event
  const handleDragLeave = useCallback(
    createHandleDragLeave(draggedOver, setDraggedOver),
    [draggedOver, setDraggedOver]
  );

  // Handle metadata dialog submit
  // Note: handleExecuteAction and handleActionCardClick are defined later after canvas handlers

  const handleDismissAction = useCallback((intent: string) => {
    setPendingActions(prev => prev.filter(a => a.intent !== intent));
  }, []);

  const handleDismissAllActions = useCallback(() => {
    setPendingActions([]);
  }, []);

  // Note: handleDataFormSubmit is defined later after handleExecuteAction

  const handleInlineFeedback = useCallback(async (actionIntent: string, feedbackType: 'upvote' | 'downvote') => {
    // Feedback logic (simplified)
    await submitFeedback({
      actionIntent,
      query: currentUserQuery,
      feedbackType,
      wasExecuted: false,
      context: { screen: 'EasyModeScreen', companyId: globalUser?.currentCompany?._id }
    });
  }, [currentUserQuery, globalUser]);

  const handlePostExecutionFeedback = useCallback(async (feedbackType: 'upvote' | 'downvote', comment?: string) => {
    if (!lastExecutedAction) return;
    await submitFeedback({
      actionIntent: lastExecutedAction.template.intent,
      query: currentUserQuery,
      feedbackType,
      wasExecuted: true,
      executionSuccess: feedbackType === 'upvote',
      context: { screen: 'EasyModeScreen', companyId: globalUser?.currentCompany?._id }
    });
  }, [lastExecutedAction, currentUserQuery, globalUser]);

  // Analyze response content to discover relevant actions (Simulating TensorFlow classification)
  useEffect(() => {
    if (chatMessages.length > 0) {
      const lastMessage = chatMessages[0];
      // Only suggest actions for AI responses
      // Skip messages that have formSchema or actionCards (these are system-generated)
      if (lastMessage.user?._id !== globalUser && !lastMessage.formSchema && !lastMessage.actionCards) {
        const text = lastMessage.text || '';
        const actions = [];

        // Get the most recent user query for context
        const recentUserMessage = chatMessages.find(msg => msg.user._id === globalUser && msg.text);
        const query = recentUserMessage?.text || currentUserQuery || '';


        // Only show "Fill Form" chip when there's actual action context (actionCards detected)
        // Don't show it based on simple keyword matching - only when AI has detected a relevant action
        const hasActionContext = lastMessage.actionCards && Array.isArray(lastMessage.actionCards) && lastMessage.actionCards.length > 0;
        const hasActionRequiringForm = hasActionContext && lastMessage.actionCards.some((action: any) =>
          action.template && action.template.requiresData && action.template.schema
        );

        // Only show "Fill Form" if there's an action that requires a form AND no form is already shown
        if (hasActionRequiringForm && !lastMessage.formSchema) {
          // Find the first action that requires a form
          const formAction = lastMessage.actionCards.find((action: any) =>
            action.template && action.template.requiresData && action.template.schema
          );

          if (formAction && formAction.template) {
            actions.push({
              functionName: 'fill_form',
              function: () => {
                // Use handleActionCardClick to properly inject the form with the correct schema
                handleActionCardClick(formAction.template, formAction.extractedData, formAction.sourcePage);
              },
              icon: <AntDesign name="form" size={16} color={theme ? "black" : "white"} />,
              text: 'Fill Form'
            });
          }
        }

        // Detect automation/flow keywords and add action chip
        const automationKeywords = ['automate', 'automation', 'workflow', 'flow', 'sequence', 'design automation', 'create flow', 'build workflow'];
        const hasAutomationIntent = automationKeywords.some(keyword => text.toLowerCase().includes(keyword)) ||
          query.toLowerCase().includes('automate') ||
          query.toLowerCase().includes('flow');


        setSuggestedActions(actions);
      } else {
        // Clear suggested actions for form/action card messages
        setSuggestedActions([]);
      }
    }
  }, [chatMessages, globalUser, theme]);

  const handleModelPress = () => {
    Alert.alert("Model Selection", "More models coming soon!");
  };


  // Note: handleFormSubmit is defined later after handleExecuteAction

  /**
   * Context-aware AI routing:
   * If the unified window is currently showing the Data Handler, prioritize "build data model"
   * by calling the dedicated `/ai/create-schema` endpoint (same flow as CreateDataScreen/DataHandlerHeader)
   * and applying it via `parseSchema(...)`.
   */
  const runAIDataModelBuilder = useCallback(async (description: string) => {
    const prompt = String(description || '').trim();
    if (!prompt) {
      console.warn('⚠️ [EasyMode] runAIDataModelBuilder - Empty prompt, returning early');
      return;
    }

    console.log('🏗️ [EasyMode] runAIDataModelBuilder - Starting:', {
      prompt,
      endpoint: 'ai/create-schema',
      route: 'ai/create-schema'
    });

    try {
      dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Building your data model...' } });

      // Reuse existing API helper (returns axios response)
      console.log('📡 [EasyMode] runAIDataModelBuilder - Calling apis.createAIDataModel (ai/create-schema)');
      const res: any = await apis.createAIDataModel(prompt);
      console.log('📥 [EasyMode] runAIDataModelBuilder - API response received:', {
        hasData: !!res?.data,
        hasRaw: !!(res?.data ?? res)?.raw,
        responseKeys: Object.keys(res || {})
      });

      const payload = res?.data ?? res;

      // Backend sometimes returns { raw: { schemaContent: [...] } } (see DataHandlerHeader)
      const raw = payload?.raw ?? payload;

      console.log('📋 [EasyMode] runAIDataModelBuilder - Processing schema:', {
        hasRaw: !!raw,
        rawType: typeof raw,
        isString: typeof raw === 'string',
        rawPreview: typeof raw === 'string' ? raw.substring(0, 100) : 'object'
      });

      // Apply schema into Redux so DataHandler reflects it immediately
      console.log('🔄 [EasyMode] runAIDataModelBuilder - Calling parseSchema');
      await parseSchema(raw, dispatch);
      console.log('✅ [EasyMode] runAIDataModelBuilder - parseSchema completed');

      // Ensure the unified window stays/opens on Data Handler after the model is built
      setActiveWindowComponent('dataHandler');

      // Lightweight summary message for the chat
      const schemaObj = typeof raw === 'string' ? JSON.parse(raw) : raw;
      const schemaContent = schemaObj?.schemaContent || schemaObj?.schema;
      const fieldTitles = Array.isArray(schemaContent)
        ? schemaContent.map((f: any) => f?.title).filter(Boolean)
        : [];

      const summary =
        fieldTitles.length > 0
          ? `✅ Data model created and loaded into Data Handler.\n\nFields (${fieldTitles.length}): ${fieldTitles.slice(0, 12).join(', ')}${fieldTitles.length > 12 ? '…' : ''}`
          : `✅ Data model created and loaded into Data Handler.`;

      setChatMessages(prevMessages =>
        GiftedChat.append(prevMessages, [{
          _id: uniqueId(),
          text: summary,
          createdAt: new Date(),
          system: true,
          received: true
        }])
      );
    } catch (error: any) {
      console.error('❌ [EasyModeScreen] Error building data model:', error);
      setChatMessages(prevMessages =>
        GiftedChat.append(prevMessages, [{
          _id: uniqueId(),
          text: `Sorry, I couldn’t build a data model from that prompt. ${error?.message ? `(${error.message})` : ''}`,
          createdAt: new Date(),
          system: true,
          received: true
        }])
      );
    } finally {
      dispatch({ type: HIDE_LOADING_MODAL });
    }
  }, [dispatch]);

  const handleQuery = useCallback(async (query: string) => {
    if (!query || !query.trim()) return;

    console.log('🚀 [EasyMode] handleQuery - Starting:', {
      query,
      activeWindowComponent,
      timestamp: new Date().toISOString()
    });

    // Prevent double submissions - check and set atomically
    if (isProcessingRef.current) {
      console.log("⚠️ [EasyMode] Query already processing, ignoring duplicate request:", query.substring(0, 50));
      return;
    }

    // Extract any inline data references ("/" selector) so we can use them immediately for reports
    const { dataIds: inlineDataIds, cleanedText: queryForIntent } = parseInlineDataRefs(query);

    console.log('📝 [EasyMode] handleQuery - Parsed query:', {
      original: query,
      cleaned: queryForIntent,
      inlineDataIds
    });

    // Set processing flag immediately
    isProcessingRef.current = true;

    // Clear voice input text when message is sent (stops recording and ignores pending results)
    await clearVoiceInput();

    // Store current user query for action analysis
    setCurrentUserQuery(queryForIntent || query);

    // Don't auto-show hierarchy - only show when user explicitly requests it or when commands are received

    if (!chatMessages.length) {
      addDisclaimer();
    }

    // Add user message first
    const userMessage = {
      _id: Math.random().toString(36).substring(7),
      text: query,
      createdAt: new Date(),
      user: { _id: globalUser, avatar: userAvatar?.img }
    };

    // Update chat messages and get the updated list for history
    let updatedMessages: any[] = [];
    setChatMessages(previousMessages => {
      updatedMessages = GiftedChat.append(previousMessages, [userMessage]);
      return updatedMessages;
    });

    setInputDisabled(true);

    // Store reference to image data and flag before try block (for cleanup in catch/finally)
    const imageData = pendingImageAnalysis?.imageData;
    const hasImage = !!pendingImageAnalysis;

    try {
      // If Data Handler is open in the unified window, treat prompts as "build data model"
      // and route through the create-schema flow (instead of general chat help).
      console.log('🔍 [EasyMode] handleQuery - Checking activeWindowComponent:', {
        activeWindowComponent,
        isDataHandler: activeWindowComponent === 'dataHandler',
        route: activeWindowComponent === 'dataHandler' ? 'ai/create-schema (direct)' : 'intent detection'
      });

      if (activeWindowComponent === 'dataHandler') {
        console.log('✅ [EasyMode] handleQuery - DataHandler is open, routing directly to runAIDataModelBuilder');
        await runAIDataModelBuilder(queryForIntent || query);
        // Clear any pending image analysis after use (no image support in schema builder path)
        if (hasImage) {
          setPendingImageAnalysis(null);
        }
        return;
      }

      // Quick keyword check for document creation (before semantic mapper)
      const normalizedQuery = (queryForIntent || query).toLowerCase().trim();
      const documentCreationKeywords = [
        'create a business plan',
        'create business plan',
        'make a pitch deck',
        'make pitch deck',
        'generate proposal',
        'create document',
        'build presentation',
        'design document',
        'create proposal',
        'build document',
        'design presentation'
      ];

      const isDocumentCreationQuery = documentCreationKeywords.some(keyword =>
        normalizedQuery.includes(keyword) || normalizedQuery === keyword
      );

      if (isDocumentCreationQuery) {
        console.log('📄 [EasyMode] Document creation keyword detected, checking semantic mapper...');
        // Still check semantic mapper, but we know it's likely a document creation intent
      }

      // Detect AI creation intents (dataModel, imageGenerate, imageEnhance) and auto-route
      // This happens before action detection to provide faster, more direct routing
      const hasSelectedImageLayer = selectedCanvasLayerId && canvasLayers.find(l => l.id === selectedCanvasLayerId)?.type === 'image';

      console.log('🔍 [EasyMode] Intent Detection - Starting:', {
        query: queryForIntent || query,
        activeWindowComponent,
        hasSelectedImageLayer,
        isDocumentCreationQuery
      });

      const intentResult = await detectAICreationIntent(
        queryForIntent || query,
        activeWindowComponent,
        hasSelectedImageLayer
      );

      console.log('🎯 [EasyMode] Intent Detection - Result:', {
        intent: intentResult.intent,
        confidence: intentResult.confidence,
        metadata: intentResult.metadata,
        query: queryForIntent || query
      });

      // Route to appropriate handler if high confidence intent detected
      // Use lower threshold for dataModel (0.5) vs imageGenerate (0.7) for faster detection
      const confidenceThreshold = intentResult.intent === 'dataModel' ? 0.5 : 0.7;

      console.log('📊 [EasyMode] Intent Routing - Threshold Check:', {
        intent: intentResult.intent,
        confidence: intentResult.confidence,
        threshold: confidenceThreshold,
        meetsThreshold: intentResult.confidence >= confidenceThreshold,
        isGeneral: intentResult.intent === 'general',
        willRoute: intentResult.confidence >= confidenceThreshold && intentResult.intent !== 'general'
      });

      if (intentResult.confidence >= confidenceThreshold && intentResult.intent !== 'general') {
        console.log('✅ [EasyMode] Intent Routing - Routing to specialized handler:', intentResult.intent);

        if (intentResult.intent === 'dataModel') {
          console.log('🗄️ [EasyMode] Data Model Intent - Routing to runAIDataModelBuilder');
          // Auto-open dataHandler window if not already open
          if ((activeWindowComponent as WindowComponentType) !== 'dataHandler') {
            dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Opening Data Handler...' } });
            setActiveWindowComponent('dataHandler');
            // Small delay to allow window animation
            await new Promise(resolve => setTimeout(resolve, 300));
            dispatch({ type: HIDE_LOADING_MODAL });
          }
          console.log('🚀 [EasyMode] Data Model - Calling runAIDataModelBuilder with:', queryForIntent || query);
          await runAIDataModelBuilder(queryForIntent || query);
          console.log('✅ [EasyMode] Data Model - runAIDataModelBuilder completed');
          if (hasImage) {
            setPendingImageAnalysis(null);
          }
          isProcessingRef.current = false;
          setInputDisabled(false);
          return;
        } else if (intentResult.intent === 'imageGenerate') {
          // Auto-open canvas window if not already open
          if (activeWindowComponent !== 'canvas') {
            dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Opening Canvas...' } });
            setActiveWindowComponent('canvas');
            await new Promise(resolve => setTimeout(resolve, 300));
            dispatch({ type: HIDE_LOADING_MODAL });
          }
          // Extract image generation prompt (remove command words)
          const imagePrompt = (queryForIntent || query)
            .replace(/generate\s+(an?\s+)?image\s+of/i, '')
            .replace(/create\s+(an?\s+)?image\s+of/i, '')
            .replace(/make\s+(a\s+)?picture\s+of/i, '')
            .replace(/draw\s+(me\s+)?(an?\s+)?/i, '')
            .trim();
          await handleCanvasAIGenerate(imagePrompt || queryForIntent || query);
          if (hasImage) {
            setPendingImageAnalysis(null);
          }
          isProcessingRef.current = false;
          setInputDisabled(false);
          return;
        } else if (intentResult.intent === 'imageEnhance') {
          // Auto-open canvas window if not already open
          if (activeWindowComponent !== 'canvas') {
            dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Opening Canvas...' } });
            setActiveWindowComponent('canvas');
            await new Promise(resolve => setTimeout(resolve, 300));
            dispatch({ type: HIDE_LOADING_MODAL });
          }
          // Check if we have a selected image layer to enhance
          if (selectedCanvasLayerId && hasSelectedImageLayer) {
            const enhancementType = intentResult.metadata?.enhancementType || 'upscale';
            await handleCanvasAIEnhance(enhancementType);
          } else {
            // No image selected, show message
            setChatMessages(prevMessages =>
              GiftedChat.append(prevMessages, [{
                _id: uniqueId(),
                text: "Please select an image layer in the canvas to enhance it.",
                createdAt: new Date(),
                system: true,
                received: true
              }])
            );
          }
          if (hasImage) {
            setPendingImageAnalysis(null);
          }
          isProcessingRef.current = false;
          setInputDisabled(false);
          return;
        }
      } else {
        console.log('⚠️ [EasyMode] Intent Routing - Not routing to specialized handler:', {
          reason: intentResult.confidence < confidenceThreshold
            ? `Confidence ${intentResult.confidence} below threshold ${confidenceThreshold}`
            : intentResult.intent === 'general'
              ? 'Intent is general'
              : 'Unknown reason',
          intent: intentResult.intent,
          confidence: intentResult.confidence,
          threshold: confidenceThreshold
        });
        console.log('📤 [EasyMode] Intent Routing - Falling through to handleAiHelp (ai/help route)');
      }

      // Detect relevant actions BEFORE sending query
      let actionSchemas = [];
      if (isMapperInitialized) {
        console.log('🔍 [EasyMode] Action Detection - Starting semantic action mapper analysis');
        console.log('🔍 [EasyMode] Query for action detection:', queryForIntent || query);
        try {
          const detectedActions = await analyzeResponse(queryForIntent || query, 'EasyModeScreen', {
            screen: 'EasyModeScreen',
            mode: mode,
            hasEditor: isEditorConnected
          });
          console.log('🎯 [EasyMode] Detected actions:', {
            count: detectedActions.length,
            actions: detectedActions.map(a => ({
              intent: a.template.intent,
              confidence: a.confidence,
              description: a.template.description
            }))
          });

          // If document creation keyword detected but semantic mapper didn't find it, manually add it
          if (isDocumentCreationQuery && !detectedActions.find(a => a.template.intent === 'CREATE_DOCUMENT')) {
            console.log('📄 [EasyMode] Document creation keyword detected but not found by semantic mapper, manually adding...');
            const registeredTemplates = getRegisteredTemplates();
            const documentTemplate = registeredTemplates.find(t => t.intent === 'CREATE_DOCUMENT');
            if (documentTemplate) {
              detectedActions.push({
                intent: 'CREATE_DOCUMENT',
                confidence: 0.85, // High confidence for keyword match
                originalConfidence: 0.85,
                feedbackMultiplier: 1.0,
                template: documentTemplate,
                sourcePage: 'EasyModeScreen',
                sourceContext: { screen: 'EasyModeScreen', mode: mode, hasEditor: isEditorConnected }
              });
              console.log('✅ [EasyMode] Manually added CREATE_DOCUMENT action with confidence 0.85');
            }
          }

          if (detectedActions.length > 0) {
            // Check for CREATE_REPORT_DASHBOARD action
            const reportsAction = detectedActions.find(
              action => action.template.intent === 'CREATE REPORT DASHBOARD'
            );

            if (reportsAction) {
              // If user already referenced data via the "/" selector, skip the selector UI
              if (inlineDataIds.length > 0) {
                handleDataSourceSelection(inlineDataIds);
              } else {
                // Otherwise, show data source selector
                handleReportsKeyword();
              }
              isProcessingRef.current = false;
              setInputDisabled(false);
              return; // Don't send to AI, handle locally
            }

            // Check for CREATE_DOCUMENT action
            const documentAction = detectedActions.find(
              action => action.template.intent === 'CREATE_DOCUMENT'
            );

            if (documentAction) {
              console.log('📄 [EasyMode] CREATE_DOCUMENT action detected:', documentAction);
              // Execute the document creation action
              // This will show the form if requiresData is true
              handleExecuteAction(documentAction.template, undefined, 'EasyModeScreen');
              isProcessingRef.current = false;
              setInputDisabled(false);
              return; // Don't send to AI, handle locally
            }

            // Collect ALL detected actions (not just those requiring data)
            // The backend will match the query against all actions and return the best match
            actionSchemas = detectedActions.map(action => {
              const schema = getSchemaForAction(action.template.intent) || action.template.schema;
              return {
                intent: action.template.intent,
                description: action.template.description || action.template.intent,
                confidence: action.confidence,
                schema: schema, // Include schema even if action doesn't require data
                category: action.template.category,
                requiresData: action.template.requiresData || false
              };
            });
          }
        } catch (error) {
          console.error('❌ Error detecting actions before query:', error);
        }
      }

      // Use handleAiHelp utility function with action schemas
      // Use updatedMessages (includes the new user message) for accurate history
      // Include image data if available (backend will analyze it)

      console.log('📡 [EasyMode] Routing Decision - Calling handleAiHelp (ai/help endpoint):', {
        query: queryForIntent || query,
        actionSchemasCount: actionSchemas.length,
        actionSchemas: actionSchemas.map(a => ({ intent: a.intent, confidence: a.confidence })),
        mode,
        hasImage: !!imageData,
        route: 'ai/help'
      });

      await handleAiHelp(isEditorConnected, query, convertGiftedChatToHistory(updatedMessages), actionSchemas, mode, null, imageData)
        .then(response => {
          // Type guard: check if response has a body with getReader (for streaming)
          // The mockResponse from handleHelp has body.getReader, not instanceof Response
          const responseAny = response as any;
          const hasStreamingBody = responseAny &&
            (responseAny instanceof Response ||
              (responseAny.body && typeof responseAny.body.getReader === 'function') ||
              (typeof responseAny.getReader === 'function'));

          if (hasStreamingBody) {
            // Check if it's a real Response object
            if (responseAny instanceof Response) {
              if (!responseAny.ok) {
                throw new Error('Network response was not ok.');
              }
            } else if (responseAny.ok === false) {
              throw new Error(`Network response was not ok: ${responseAny.status || 'unknown status'}`);
            }

            // Get reader from body.getReader or direct getReader
            const reader = responseAny.body?.getReader
              ? responseAny.body.getReader()
              : (responseAny.getReader ? responseAny.getReader() : null);

            if (!reader) {
              throw new Error('Response does not support streaming');
            }
            const decoder = new TextDecoder();
            let accumulatedData = '';

            const processStream = async () => {
              const uid = uniqueId();

              // Create a message with the ID
              setChatMessages(prevMessages => GiftedChat.append(prevMessages, [{
                _id: uid,
                text: 'thinking...',
                createdAt: new Date(),
                system: true,
                received: true  // Immediately mark as received since no animation
              }]));

              try {
                while (true) {
                  const { done, value } = await reader.read();
                  if (done) break; // Stream is finished

                  // Decode and accumulate the data
                  const decodedChunk = decoder.decode(value, { stream: true });
                  accumulatedData += decodedChunk;

                  // Handle concatenated JSON objects (missing newlines)
                  accumulatedData = accumulatedData.replace(/}\s*{/g, '}\n{');

                  // Process newline-separated JSON chunks (streaming format)
                  const lines = accumulatedData.split('\n');

                  // Keep the last potentially incomplete line
                  let lastLine = lines.pop() || '';

                  // Try to parse the last line - if it's valid JSON, process it now
                  // This handles cases where the stream flushes complete objects without newlines
                  if (lastLine.trim()) {
                    try {
                      JSON.parse(lastLine);
                      // If parse successful, treat as complete line
                      lines.push(lastLine);
                      lastLine = '';
                    } catch (e) {
                      // If parse failed, it's likely incomplete, keep in buffer
                    }
                  }

                  accumulatedData = lastLine;

                  // Process each complete line
                  for (const line of lines) {
                    if (!line.trim()) continue; // Skip empty lines

                    try {
                      // Keep input disabled during streaming (don't re-enable in loop)

                      // Parse JSON from line
                      let data;
                      try {
                        data = JSON.parse(line);
                      } catch (err) {
                        // If JSON parsing fails, skip this line
                        console.warn('Failed to parse JSON line:', line.substring(0, 100));
                        continue;
                      }

                      // Ensure data.response is a string before processing
                      const responseText = typeof data.response === 'string' ? data.response : JSON.stringify(data.response);

                      // Update message text
                      setChatMessages(prevMessages => {
                        const updatedMessages = prevMessages.map(message => {
                          if (message._id === uid) {
                            // Replace "thinking..." with actual response when first chunk arrives
                            const currentText = message.text || '';
                            const newText = currentText === 'thinking...'
                              ? responseText
                              : currentText + responseText;
                            return {
                              ...message,
                              text: newText,
                              received: true,
                              streamHash: uniqueId('stream_')
                            };
                          }
                          return message;
                        });

                        // Add the message if it doesn't exist
                        if (!updatedMessages.some(message => message._id === uid)) {
                          updatedMessages.push({
                            _id: uid,
                            text: responseText,
                            createdAt: new Date(),
                            system: true,
                            received: true
                          });
                        }
                        //Pass focus back into text input
                        return updatedMessages;
                      });

                      // Process automation flow commands
                      if (data.EXTRA?.automation_flow_commands || data.automation_flow_commands) {
                        const commands = parseAutomationFlowCommands(data);
                        if (commands.length > 0) {

                          // Show hierarchy automatically when first command received
                          if (!showHierarchy) {
                            handleToggleWindow('hierarchy');
                          }

                          // Process each command
                          commands.forEach(command => {
                            if (hierarchyRef.current) {
                              hierarchyRef.current.processCommand(command);
                              // Update hasHierarchyActions after processing
                              setTimeout(() => {
                                const aiActions = hierarchyRef.current?.getAIManagedActions() || [];
                                setHasHierarchyActions(aiActions.length > 0 || reduxActions.length > 0);
                              }, 100);
                            }
                          });
                        }
                      }

                      // Process plan questions
                      if (mode === 'plan' || data.EXTRA?.plan_questions) {
                        try {
                          const questions = parsePlanQuestions(data, mode);
                          if (questions.length > 0) {

                            setChatMessages(prevMessages =>
                              prevMessages.map(msg => {
                                if (msg._id === uid) {
                                  return {
                                    ...msg,
                                    planQuestions: questions
                                  };
                                }
                                return msg;
                              })
                            );
                          }
                        } catch (error) {
                          console.error('❌ Error parsing plan questions:', error);
                        }
                      }

                      // Check for EXTRA object with best match
                      if (data.EXTRA && data.EXTRA.best_match) {
                        const bestMatch = data.EXTRA.best_match;

                        // Find the actual action template from registered templates
                        let actualTemplate = null;
                        if (isMapperInitialized) {
                          try {
                            const registeredTemplates = getRegisteredTemplates();
                            actualTemplate = registeredTemplates.find(t => t.intent === bestMatch.intent);
                          } catch (error) {
                            console.error('Error getting registered templates:', error);
                          }
                        }

                        // Always attach action cards when there's a best match
                        // Don't auto-inject forms - let user click the action card to open form
                        if (actualTemplate) {
                          // Try to extract data from query (for pre-filling if user clicks)
                          let extractedData = {};
                          if (currentUserQuery && bestMatch.schema) {
                            try {
                              const extraction = await extractDataHybrid(
                                currentUserQuery,
                                bestMatch.schema,
                                bestMatch.description || bestMatch.intent
                              );
                              extractedData = extraction.extractedData || {};
                            } catch (error) {
                              console.error('Error extracting data:', error);
                            }
                          }

                          setChatMessages(prevMessages =>
                            prevMessages.map(msg => {
                              if (msg._id === uid) {
                                return {
                                  ...msg,
                                  sourcePage: 'EasyModeScreen', // Track source page for badge increment
                                  actionCards: [{
                                    intent: bestMatch.intent,
                                    confidence: bestMatch.match_confidence || bestMatch.confidence || 0.8,
                                    template: actualTemplate,
                                    extractedData: extractedData, // Pre-extracted data for form pre-filling
                                    sourcePage: 'EasyModeScreen' // Include in action card for badge tracking
                                  }]
                                };
                              }
                              return msg;
                            })
                          );
                        }
                      }
                    } catch (err) {
                      // Log error if needed
                      console.error('Error processing data:', err);
                    }
                  }
                }
              } catch (streamError) {
                // Handle stream reading errors
                console.error('❌ [EasyMode] Stream reading error:', streamError);
                // Don't re-enable here - let finally block handle it
              }

              // Finalize any remaining data after stream ends
              if (accumulatedData) {
                try {
                  const data = JSON.parse(accumulatedData);
                  // Ensure data.response is a string before processing
                  const responseText = typeof data.response === 'string' ? data.response : JSON.stringify(data.response);

                  setChatMessages(prevMessages => {
                    const updatedMessages = prevMessages.map(message => {
                      if (message._id === uid) {
                        // Replace "thinking..." with actual response if still present
                        const currentText = message.text || '';
                        const newText = currentText === 'thinking...'
                          ? responseText
                          : currentText + responseText;
                        return { ...message, text: newText };
                      }
                      return message;
                    });

                    if (!updatedMessages.some(message => message._id === uid)) {
                      updatedMessages.push({
                        _id: uid,
                        text: responseText,
                        createdAt: new Date(),
                        system: true
                      });
                    }

                    return updatedMessages;
                  });

                  // Process automation flow commands after stream completes
                  if (data.EXTRA?.automation_flow_commands || data.automation_flow_commands) {
                    const commands = parseAutomationFlowCommands(data);
                    if (commands.length > 0) {
                      console.log('🤖 Processing automation flow commands (stream end):', commands);

                      if (!showHierarchy) {
                        handleToggleWindow('hierarchy');
                      }

                      commands.forEach(command => {
                        if (hierarchyRef.current) {
                          hierarchyRef.current.processCommand(command);
                        }
                      });
                    }
                  }

                  // Process plan questions after stream completes
                  if (mode === 'plan' || data.EXTRA?.plan_questions) {
                    try {
                      const questions = parsePlanQuestions(data, mode);
                      if (questions.length > 0) {
                        console.log('❓ [EasyMode] Detected plan questions (stream end):', questions.length);

                        setChatMessages(prevMessages =>
                          prevMessages.map(msg => {
                            if (msg._id === uid) {
                              return {
                                ...msg,
                                planQuestions: questions
                              };
                            }
                            return msg;
                          })
                        );
                      }
                    } catch (error) {
                      console.error('❌ Error parsing plan questions (stream end):', error);
                    }
                  }

                  // Check for EXTRA object with best match after stream completes
                  if (data.EXTRA && data.EXTRA.best_match) {
                    const bestMatch = data.EXTRA.best_match;
                    console.log('🎯 AI detected best match (stream end):', bestMatch);

                    // Find the actual action template from registered templates
                    let actualTemplate = null;
                    if (isMapperInitialized) {
                      try {
                        const registeredTemplates = getRegisteredTemplates();
                        actualTemplate = registeredTemplates.find(t => t.intent === bestMatch.intent);
                      } catch (error) {
                        console.error('Error getting registered templates:', error);
                      }
                    }

                    // Always attach action cards when there's a best match (stream end)
                    // Don't auto-inject forms - let user click the action card to open form
                    if (actualTemplate) {
                      // Try to extract data from query (for pre-filling if user clicks)
                      let extractedData = {};
                      if (currentUserQuery && bestMatch.schema) {
                        try {
                          const extraction = await extractDataHybrid(
                            currentUserQuery,
                            bestMatch.schema,
                            bestMatch.description || bestMatch.intent
                          );
                          extractedData = extraction.extractedData || {};
                        } catch (error) {
                          console.error('Error extracting data:', error);
                        }
                      }

                      setChatMessages(prevMessages =>
                        prevMessages.map(msg => {
                          if (msg._id === uid) {
                            return {
                              ...msg,
                              sourcePage: 'EasyModeScreen', // Track source page for badge increment
                              actionCards: [{
                                intent: bestMatch.intent,
                                confidence: bestMatch.match_confidence || bestMatch.confidence || 0.8,
                                template: actualTemplate,
                                extractedData: extractedData, // Pre-extracted data for form pre-filling
                                sourcePage: 'EasyModeScreen' // Include in action card for badge tracking
                              }]
                            };
                          }
                          return msg;
                        })
                      );
                    }
                  } else if (!data.EXTRA || !data.EXTRA.best_match) {
                    // Fallback: Analyze user query for actionable intents if no EXTRA match
                    if (isMapperInitialized && query.trim().length > 0) {
                      analyzeResponse(query, 'EasyModeScreen', {
                        screen: 'EasyModeScreen',
                        mode: mode,
                        hasEditor: isEditorConnected
                      }).then(async matches => {
                        if (matches.length > 0) {

                          // Extract data for each action that requires data
                          const actionsWithData = await Promise.all(
                            matches.map(async (match: MatchedAction) => {
                              let extractedData = {};
                              if (match.template.requiresData && match.template.schema && query) {
                                try {
                                  const schema = getSchemaForAction(match.template.intent) || match.template.schema;
                                  const extraction = await extractDataHybrid(
                                    query,
                                    schema,
                                    match.template.description || match.template.intent
                                  );
                                  extractedData = extraction.extractedData || {};
                                } catch (error) {
                                  console.error(`Error extracting data for ${match.intent}:`, error);
                                }
                              }
                              return {
                                ...match,
                                extractedData // Attach extracted data to the matched action
                              };
                            })
                          );

                          // Attach action cards to the existing response message instead of creating a new one
                          // Only attach if the message doesn't already have action cards
                          setChatMessages(prevMessages =>
                            prevMessages.map(msg => {
                              if (msg._id === uid && !msg.actionCards) {
                                return {
                                  ...msg,
                                  sourcePage: 'EasyModeScreen', // Track source page
                                  actionCards: actionsWithData.map(action => ({
                                    ...action,
                                    sourcePage: action.sourcePage || 'EasyModeScreen' // Ensure sourcePage is set
                                  }))
                                };
                              }
                              return msg;
                            })
                          );
                        }
                      }).catch(error => {
                        console.error('❌ Failed to analyze user query:', error);
                      });
                    }
                  }
                } catch (err) {
                  console.error('Error processing line:', err);
                }
              }
            };

            processStream()
              .then(() => {
                // Stream completed successfully
                console.log('✅ [EasyMode] Stream processing completed');
                // Clear pending image analysis after successful send
                if (hasImage) {
                  setPendingImageAnalysis(null);
                }
              })
              .catch((streamError) => {
                // Stream processing failed
                console.error('❌ [EasyMode] Stream processing failed:', streamError);
                // Clear pending image analysis even on error
                if (hasImage) {
                  setPendingImageAnalysis(null);
                }
              })
              .finally(() => {
                // Always re-enable input when stream ends (success or error)
                setInputDisabled(false);
                //Pass focus back into text input
                const focusTimeoutId = setTimeout(() => inputRef.current?.focus(), 50);
                timeoutIdsRef.current.push(focusTimeoutId);
              });
          } else {
            // Handle non-streaming JSON response (fallback)
            const jsonResponse = response as any;
            const uid = uniqueId();
            let responseText = '';
            if (jsonResponse.response) {
              responseText = typeof jsonResponse.response === 'string' ? jsonResponse.response : JSON.stringify(jsonResponse.response);
            } else if (jsonResponse.context) {
              responseText = typeof jsonResponse.context === 'string' ? jsonResponse.context : JSON.stringify(jsonResponse.context);
            } else {
              responseText = JSON.stringify(jsonResponse);
            }

            const cleanResponseText = responseText.replace(/<think>/g, '').replace(/<\/think>/g, '');

            setChatMessages(prevMessages =>
              GiftedChat.append(prevMessages, [{
                _id: uid,
                text: cleanResponseText,
                createdAt: new Date(),
                system: true,
                received: true
              }])
            );

            // Process automation flow commands in JSON response
            if (jsonResponse.EXTRA?.automation_flow_commands || jsonResponse.automation_flow_commands) {
              const commands = parseAutomationFlowCommands(jsonResponse);
              if (commands.length > 0) {

                if (!showHierarchy) {
                  handleToggleWindow('hierarchy');
                }

                commands.forEach(command => {
                  if (hierarchyRef.current) {
                    hierarchyRef.current.processCommand(command);
                  }
                });
              }
            }

            // Process plan questions in JSON response
            if (mode === 'plan' || jsonResponse.EXTRA?.plan_questions) {
              try {
                const questions = parsePlanQuestions(jsonResponse, mode);
                if (questions.length > 0) {

                  setChatMessages(prevMessages =>
                    prevMessages.map(msg => {
                      if (msg._id === uid) {
                        return {
                          ...msg,
                          planQuestions: questions
                        };
                      }
                      return msg;
                    })
                  );
                }
              } catch (error) {
                console.error('❌ Error parsing plan questions (JSON response):', error);
              }
            }

            // Check for EXTRA object with best match in JSON response (async handler)
            (async () => {
              if (jsonResponse.EXTRA && jsonResponse.EXTRA.best_match) {
                const bestMatch = jsonResponse.EXTRA.best_match;

                // Find the actual action template from registered templates
                let actualTemplate = null;
                if (isMapperInitialized) {
                  try {
                    const registeredTemplates = getRegisteredTemplates();
                    actualTemplate = registeredTemplates.find(t => t.intent === bestMatch.intent);
                  } catch (error) {
                    console.error('Error getting registered templates:', error);
                  }
                }

                // Always attach action cards when there's a best match (non-streaming)
                // Don't auto-inject forms - let user click the action card to open form
                if (actualTemplate) {
                  // Try to extract data from query (for pre-filling if user clicks)
                  let extractedData = {};
                  if (query && bestMatch.schema) {
                    try {
                      const extraction = await extractDataHybrid(
                        query,
                        bestMatch.schema,
                        bestMatch.description || bestMatch.intent
                      );
                      extractedData = extraction.extractedData || {};
                    } catch (error) {
                      console.error('Error extracting data:', error);
                    }
                  }



                  setChatMessages(prevMessages =>
                    prevMessages.map(msg => {
                      if (msg._id === uid) {
                        return {
                          ...msg,
                          sourcePage: 'EasyModeScreen', // Track source page for badge increment
                          actionCards: [{
                            intent: bestMatch.intent,
                            confidence: bestMatch.match_confidence || bestMatch.confidence || 0.8,
                            template: actualTemplate,
                            extractedData: extractedData, // Pre-extracted data for form pre-filling
                            sourcePage: 'EasyModeScreen' // Include in action card for badge tracking
                          }]
                        };
                      }
                      return msg;
                    })
                  );
                }
              } else if (!jsonResponse.EXTRA || !jsonResponse.EXTRA.best_match) {
                // Fallback: Analyze user query for actionable intents if no EXTRA match
                if (isMapperInitialized && query.trim().length > 0) {
                  analyzeResponse(query).then(async matches => {
                    if (matches.length > 0) {
                      // Extract data for each action that requires data
                      const actionsWithData = await Promise.all(
                        matches.map(async (match: MatchedAction) => {
                          let extractedData = {};
                          if (match.template.requiresData && match.template.schema && query) {
                            try {
                              const schema = getSchemaForAction(match.template.intent) || match.template.schema;
                              const extraction = await extractDataHybrid(
                                query,
                                schema,
                                match.template.description || match.template.intent
                              );
                              extractedData = extraction.extractedData || {};
                            } catch (error) {
                              console.error(`Error extracting data for ${match.intent}:`, error);
                            }
                          }
                          return {
                            ...match,
                            extractedData
                          };
                        })
                      );

                      // Attach action cards to the existing response message
                      // Only attach if the message doesn't already have action cards
                      setChatMessages(prevMessages =>
                        prevMessages.map(msg => {
                          if (msg._id === uid && !msg.actionCards) {
                            return { ...msg, actionCards: actionsWithData };
                          }
                          return msg;
                        })
                      );
                    }
                  }).catch(error => {
                    console.error('❌ Failed to analyze user query:', error);
                  });
                }
              }
            })();

            setInputDisabled(false);
            const focusTimeoutId = setTimeout(() => inputRef.current?.focus(), 50);
            timeoutIdsRef.current.push(focusTimeoutId);
          }
        })
        .catch(err => {
          console.error('❌ [EasyMode] Error in handleAiHelp:', err);
          setChatMessages(previousMessages =>
            GiftedChat.append(previousMessages, [
              {
                _id: Math.random().toString(36).substring(7),
                text: 'Sorry, I am not able to answer that right now. Please try again.',
                createdAt: new Date(),
                system: true,
                isRetryable: true,
                retryQuery: query
              }
            ])
          );
          // Clear pending image analysis on error
          if (hasImage) {
            setPendingImageAnalysis(null);
          }
        });
    } catch (err) {
      console.error('❌ [EasyMode] Error in handleQuery:', err);
      setChatMessages(previousMessages =>
        GiftedChat.append(previousMessages, [
          {
            _id: Math.random().toString(36).substring(7),
            text: 'Sorry, I am not able to answer that right now. Please try again.',
            createdAt: new Date(),
            system: true,
            isRetryable: true,
            retryQuery: query
          }
        ])
      );
      // Clear pending image analysis on error
      if (hasImage) {
        setPendingImageAnalysis(null);
      }
    } finally {
      // Always reset processing flag and input state, even on error
      isProcessingRef.current = false;
      setInputDisabled(false);
      const focusTimeoutId = setTimeout(() => inputRef.current?.focus(), 50);
      timeoutIdsRef.current.push(focusTimeoutId);
    }
  }, [chatMessages, globalUser, userAvatar?.img, isEditorConnected, isMapperInitialized, clearVoiceInput, activeWindowComponent, runAIDataModelBuilder, queryClient]);

  // Handle retry for failed prompts
  const handleRetryQuery = useCallback((messageId: string, retryQuery: string) => {
    // Prevent duplicate retries - check if already processing
    if (isProcessingRef.current) {
      console.log("⚠️ [EasyMode] Retry already processing, ignoring duplicate retry request");
      return;
    }

    // Remove the error message
    setChatMessages(prevMessages => prevMessages.filter(msg => msg._id !== messageId));
    // Re-send the original query
    if (retryQuery && retryQuery.trim()) {
      handleQuery(retryQuery);
    }
  }, [handleQuery]);

  const handleQuestionSubmit = useCallback((answers: Record<string, string[]>, messageId?: string) => {

    // Find the message with questions
    const questionMessage = messageId
      ? chatMessages.find(msg => msg._id === messageId)
      : chatMessages.find(msg => msg.planQuestions && Array.isArray(msg.planQuestions) && msg.planQuestions.length > 0);

    if (!questionMessage || !questionMessage.planQuestions) {
      console.error('❌ [EasyMode] Could not find question message');
      return;
    }

    // Mark questions as answered
    setChatMessages(prev => prev.map(msg => {
      if (msg._id === questionMessage._id) {
        return {
          ...msg,
          selectedAnswers: answers,
          questionsAnswered: true
        };
      }
      return msg;
    }));

    // Format answers for submission
    const formattedAnswers = formatAnswersForSubmission(questionMessage.planQuestions, answers);

    // Send formatted answers as a follow-up query
    handleQuery(formattedAnswers);
  }, [chatMessages, handleQuery]);

  // Use utility function for addDisclaimer
  const addDisclaimer = useCallback(() => {
    addDisclaimerUtil(setChatMessages);
  }, []);

  // Cleanup all timeouts and reset flags on unmount
  useEffect(() => {
    return () => {
      timeoutIdsRef.current.forEach(timeoutId => clearTimeout(timeoutId));
      timeoutIdsRef.current = [];
      isClosingRef.current = false;
      isProcessingRef.current = false;
      // Note: We don't clear chatFingerprintRef on unmount
      // Dismissed state is now in Redux and persists across navigation
    };
  }, []);


  // Function to save chat conversation using utility
  const saveChatConversation = useCallback(
    createSaveChatConversation({
      chatMessages,
      globalUser,
      userData,
      isEditorConnected,
      hasChatToSave: hasChatToSaveMemo,
    }),
    [chatMessages, globalUser, userData, isEditorConnected, hasChatToSaveMemo]
  );

  // Function to handle closing Easy Mode with save prompt
  const handleCloseWithPrompt = useCallback(async () => {
    // Prevent multiple simultaneous close attempts
    if (isClosingRef.current) {
      return false;
    }

    // Get fingerprint once and check conditions consistently
    const currentFingerprint = getChatFingerprintMemo();
    const hasChat = currentFingerprint !== null;
    const isDismissed = currentFingerprint ? dismissedSavePrompts.includes(currentFingerprint) : false;

    // Check if there are messages to save before closing
    if (hasChat && !isDismissed) {
      try {
        isClosingRef.current = true;

        // Show confirmation dialog using AlertDialogProvider
        const result = await showConfirm(
          'Save Chat?',
          'You have an unsaved chat conversation. Would you like to save it before leaving?',
          {
            cancelText: "Don't Save",
            confirmText: 'Save & Leave'
          }
        );

        // Handle dialog results: true = confirm, false = cancel, undefined = dismissed
        if (result === true) {
          // User confirmed - save chat and then close
          const saved = await saveChatConversation();
          if (saved) {
            // Chat saved successfully, close Easy Mode
            dispatch({ type: TOGGLE_EASY_MODE });
            isClosingRef.current = false;
            return true;
          } else {
            // Save failed, show error but still allow closing
            const leaveAnyway = await showConfirm(
              'Save Failed',
              'Could not save your chat. Do you still want to leave?',
              {
                cancelText: 'Stay',
                confirmText: 'Leave Anyway'
              }
            );

            if (leaveAnyway === true) {
              // User chose to leave anyway - mark as dismissed in Redux
              if (currentFingerprint) {
                dispatch({ type: DISMISS_EASY_MODE_SAVE_PROMPT, payload: currentFingerprint });
                // Save to backend
                const updatedPrompts = [...(dismissedSavePrompts || []), currentFingerprint];
                saveDismissedPromptsToBackend(updatedPrompts);
              }
              dispatch({ type: TOGGLE_EASY_MODE });
              isClosingRef.current = false;
              return true;
            }
            isClosingRef.current = false;
            return false;
          }
        } else if (result === false) {
          // User clicked "Don't Save" - show second confirmation to leave without saving
          const leaveWithoutSaving = await showConfirm(
            'Leave Without Saving?',
            'Your chat conversation will be lost. Are you sure you want to leave?',
            {
              cancelText: 'Stay',
              confirmText: 'Leave'
            }
          );

          if (leaveWithoutSaving === true) {
            // User confirmed leaving without saving - mark as dismissed in Redux
            if (currentFingerprint) {
              dispatch({ type: DISMISS_EASY_MODE_SAVE_PROMPT, payload: currentFingerprint });
              console.log('💬 [EasyModeScreen] Marked chat as dismissed in Redux (user chose to leave without saving)');
              // Save to backend
              const updatedPrompts = [...(dismissedSavePrompts || []), currentFingerprint];
              saveDismissedPromptsToBackend(updatedPrompts);
            }
            dispatch({ type: TOGGLE_EASY_MODE });
            isClosingRef.current = false;
            return true;
          }
          isClosingRef.current = false;
          return false;
        } else {
          // result === undefined - user dismissed dialog (e.g., clicked outside)
          // Mark as dismissed in Redux so we don't prompt again
          if (currentFingerprint) {
            dispatch({ type: DISMISS_EASY_MODE_SAVE_PROMPT, payload: currentFingerprint });
            // Save to backend
            const updatedPrompts = [...(dismissedSavePrompts || []), currentFingerprint];
            saveDismissedPromptsToBackend(updatedPrompts);
          }
          isClosingRef.current = false;
          return false;
        }
      } catch (error) {
        console.error('❌ [EasyModeScreen] Error in close dialog:', error);
        isClosingRef.current = false;
        // If dialog fails, don't close as a safety measure
        return false;
      }
    } else if (isDismissed) {
      // User previously dismissed save prompt - allow navigation without prompting
      console.log('✅ [EasyModeScreen] Prompt dismissed, allowing close');
      dispatch({ type: TOGGLE_EASY_MODE });
      return true;
    } else {
      // No messages to save, just close
      console.log('✅ [EasyModeScreen] No chat to save, allowing close');
      dispatch({ type: TOGGLE_EASY_MODE });
      return true;
    }
  }, [getChatFingerprint, dismissedSavePrompts, showConfirm, saveChatConversation, dispatch, saveDismissedPromptsToBackend]);

  const handleClose = () => {
    // For explicit close actions, use the prompt handler
    handleCloseWithPrompt();
  };

  // Expose close handler globally so other components can use it
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      // Store the close handler on window so other components can access it
      (window as any).__easyModeCloseHandler = handleCloseWithPrompt;

      return () => {
        delete (window as any).__easyModeCloseHandler;
      };
    }
  }, [handleCloseWithPrompt]);

  // Handle React Navigation away from Easy Mode
  useFocusEffect(
    useCallback(() => {
      const handleBeforeRemove = (e: any) => {
        // Get fingerprint once and check conditions consistently (same logic as handleCloseWithPrompt)
        const currentFingerprint = getChatFingerprintMemo();
        const hasChat = currentFingerprint !== null;
        const isDismissed = currentFingerprint ? (dismissedSavePrompts || []).includes?.(currentFingerprint) : false;

        console.log('🔵 [EasyModeScreen] beforeRemove triggered', {
          hasChat,
          isDismissed,
          fingerprint: currentFingerprint,
          isClosing: isClosingRef.current,
          actionType: e.data?.action?.type
        });

        // Only prompt if there are messages to save and prompt wasn't dismissed
        if (!hasChat || isDismissed) {
          // No messages to save or prompt was dismissed - allow navigation
          console.log('✅ [EasyModeScreen] Allowing navigation - no chat to save or prompt dismissed');
          dispatch({ type: TOGGLE_EASY_MODE });
          return;
        }

        // Prevent multiple simultaneous navigation attempts
        if (isClosingRef.current) {
          console.log('⏸️ [EasyModeScreen] Navigation already in progress, preventing duplicate');
          e.preventDefault();
          return;
        }

        // Prevent default navigation synchronously
        console.log('🛑 [EasyModeScreen] Preventing navigation - showing save dialog');
        e.preventDefault();

        // Store the navigation action for later dispatch
        const navigationAction = e.data.action;

        // Handle the async dialog in a separate async function
        const handleDialog = async () => {
          try {
            isClosingRef.current = true;
            console.log('💬 [EasyModeScreen] Showing save dialog');

            // Show confirmation dialog using AlertDialogProvider
            const result = await showConfirm(
              'Save Chat?',
              'You have an unsaved chat conversation. Would you like to save it before leaving?',
              {
                cancelText: "Don't Save",
                confirmText: 'Save & Leave'
              }
            );

            console.log('💬 [EasyModeScreen] Dialog result:', result);

            if (result === true) {
              // User confirmed - save chat and then navigate
              const saved = await saveChatConversation();
              if (saved) {
                // Chat saved successfully, allow navigation
                dispatch({ type: TOGGLE_EASY_MODE });
                isClosingRef.current = false;
                // Use setTimeout to ensure state updates are processed before navigation
                setTimeout(() => {
                  navigation.dispatch(navigationAction);
                }, 0);
              } else {
                // Save failed, show error but still allow navigation
                const leaveAnyway = await showConfirm(
                  'Save Failed',
                  'Could not save your chat. Do you still want to leave?',
                  {
                    cancelText: 'Stay',
                    confirmText: 'Leave Anyway'
                  }
                );

                if (leaveAnyway === true) {
                  // User chose to leave anyway - mark as dismissed in Redux
                  if (currentFingerprint) {
                    dispatch({ type: DISMISS_EASY_MODE_SAVE_PROMPT, payload: currentFingerprint });
                    console.log('💬 [EasyModeScreen] Ma rked chat as dismissed in Redux (leave anyway after save failed)');
                    // Save to backend
                    const updatedPrompts = [...(dismissedSavePrompts || []), currentFingerprint];
                    saveDismissedPromptsToBackend(updatedPrompts);
                  }
                  dispatch({ type: TOGGLE_EASY_MODE });
                  isClosingRef.current = false;
                  setTimeout(() => {
                    navigation.dispatch(navigationAction);
                  }, 0);
                } else {
                  isClosingRef.current = false;
                }
              }
            } else if (result === false) {
              // User clicked "Don't Save" - show second confirmation to leave without saving
              const leaveWithoutSaving = await showConfirm(
                'Leave Without Saving?',
                'Your chat conversation will be lost. Are you sure you want to leave?',
                {
                  cancelText: 'Stay',
                  confirmText: 'Leave'
                }
              );

              if (leaveWithoutSaving === true) {
                // User confirmed leaving without saving - mark as dismissed in Redux
                if (currentFingerprint) {
                  dispatch({ type: DISMISS_EASY_MODE_SAVE_PROMPT, payload: currentFingerprint });
                  // Save to backend
                  const updatedPrompts = [...(dismissedSavePrompts || []), currentFingerprint];
                  saveDismissedPromptsToBackend(updatedPrompts);
                }
                dispatch({ type: TOGGLE_EASY_MODE });
                isClosingRef.current = false;
                setTimeout(() => {
                  navigation.dispatch(navigationAction);
                }, 0);
              } else {
                isClosingRef.current = false;
              }
            } else {
              // result === undefined - user dismissed dialog (e.g., clicked outside)
              // Mark as dismissed in Redux so we don't prompt again
              if (currentFingerprint) {
                dispatch({ type: DISMISS_EASY_MODE_SAVE_PROMPT, payload: currentFingerprint });
                // Save to backend
                const updatedPrompts = [...(dismissedSavePrompts || []), currentFingerprint];
                saveDismissedPromptsToBackend(updatedPrompts);
              }
              isClosingRef.current = false;
            }
          } catch (error) {
            console.error('❌ [EasyModeScreen] Error in navigation dialog:', error);
            isClosingRef.current = false;
            // If dialog fails, allow navigation anyway to prevent user from being stuck
            dispatch({ type: TOGGLE_EASY_MODE });
            // Attempt to navigate even if dialog failed
            try {
              setTimeout(() => {
                navigation.dispatch(navigationAction);
              }, 0);
            } catch (navError) {
              console.error('❌ [EasyModeScreen] Navigation error after dialog failure:', navError);
            }
          }
        };

        // Start the async dialog handling
        handleDialog().catch((error) => {
          // Fallback error handler in case promise rejects
          console.error('❌ [EasyModeScreen] Dialog handler promise rejected:', error);
          isClosingRef.current = false;
          dispatch({ type: TOGGLE_EASY_MODE });
          try {
            setTimeout(() => {
              navigation.dispatch(navigationAction);
            }, 0);
          } catch (navError) {
            console.error('❌ [EasyModeScreen] Navigation error after promise rejection:', navError);
          }
        });
      };

      const unsubscribe = navigation.addListener('beforeRemove', handleBeforeRemove);

      return () => {
        unsubscribe();
        // Reset closing flag when component unmounts or loses focus
        isClosingRef.current = false;
      };
    }, [navigation, getChatFingerprint, dismissedSavePrompts, saveChatConversation, showConfirm, dispatch, saveDismissedPromptsToBackend])
  );

  // Detect automation flow intent in user query
  const detectAutomationFlowIntent = useCallback((query: string): boolean => {
    const flowKeywords = ['automation', 'workflow', 'flow', 'sequence', 'design automation', 'create flow', 'build workflow'];
    const lowerQuery = query.toLowerCase();
    return flowKeywords.some(keyword => lowerQuery.includes(keyword));
  }, []);

  // Callback to show DataHandler when file is processed
  // const handleDataFileProcessed = useCallback(() => {
  //   handleToggleWindow('dataHandler');
  // }, [handleToggleWindow]);

  // Canvas layer management
  const handleCanvasLayerUpdate = useCallback((layerId: string, updates: Partial<CanvasLayer>) => {
    setCanvasLayers(prev => prev.map(layer =>
      layer.id === layerId ? { ...layer, ...updates } : layer
    ));
  }, []);

  const handleCanvasLayerAdd = useCallback((layerData: Omit<CanvasLayer, 'id' | 'zIndex'>): string => {
    const maxZIndex = Math.max(0, ...canvasLayers.map(l => l.zIndex), 0);
    const newLayer: CanvasLayer = {
      ...layerData,
      id: `canvas-layer-${Date.now()}-${Math.random()}`,
      zIndex: maxZIndex + 1,
    };
    setCanvasLayers(prev => [...prev, newLayer]);
    return newLayer.id;
  }, [canvasLayers]);

  const handleCanvasLayerRemove = useCallback((layerId: string) => {
    setCanvasLayers(prev => prev.filter(layer => layer.id !== layerId));
    if (selectedCanvasLayerId === layerId) {
      setSelectedCanvasLayerId(null);
    }
  }, [selectedCanvasLayerId]);

  const handleMediaSelect = useCallback((imageUrl: string, imageData?: any) => {
    // Default new images to (roughly) the visible window width so they feel "full size" on add.
    // Note: layer coordinates/sizes should match the visible canvas in CanvasWindow.
    // CanvasWindow uses a 16:9 canvas sized to `windowWidth`.
    const CANVAS_W = Math.max(300, windowWidth);
    const CANVAS_H = Math.max(200, Math.round(CANVAS_W * 9 / 16));

    const targetCanvasWidth = Math.max(200, CANVAS_W - 48); // small padding for window chrome

    const metaW =
      (typeof imageData?.width === 'number' && imageData.width) ||
      (typeof imageData?.metadata?.width === 'number' && imageData.metadata.width) ||
      (typeof imageData?.meta?.width === 'number' && imageData.meta.width) ||
      undefined;
    const metaH =
      (typeof imageData?.height === 'number' && imageData.height) ||
      (typeof imageData?.metadata?.height === 'number' && imageData.metadata.height) ||
      (typeof imageData?.meta?.height === 'number' && imageData.meta.height) ||
      undefined;

    const aspect = metaW && metaH && metaW > 0 && metaH > 0 ? metaH / metaW : 1;
    const targetCanvasHeight = Math.min(CANVAS_H, Math.max(200, targetCanvasWidth * aspect));

    const posX = Math.max(0, (CANVAS_W - targetCanvasWidth) / 2);
    const posY = Math.max(0, (CANVAS_H - targetCanvasHeight) / 2);

    handleCanvasLayerAdd({
      type: 'image',
      url: imageUrl,
      position: { x: posX, y: posY },
      size: { width: targetCanvasWidth, height: targetCanvasHeight },
      rotation: 0,
      opacity: 1,
      blendMode: 'normal',
      locked: false,
    });
    setShowMediaSelector(false);
  }, [handleCanvasLayerAdd]);

  // Canvas AI enhancement
  const handleCanvasAIEnhance = useCallback(async (
    type: 'upscale' | 'stylize' | 'remove-background',
    options?: any
  ) => {
    const selectedLayer = canvasLayers.find(l => l.id === selectedCanvasLayerId);
    if (!selectedLayer || selectedLayer.type !== 'image' || !selectedLayer.url) {
      return;
    }

    try {
      setInputDisabled(true);
      // Show global loading while enhancement is running
      dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Enhancing image...' } });
      const response = await apis.enhanceProductImage(
        selectedLayer.url,
        undefined,
        type,
        2,
        options?.prompt,
        options?.style
      );

      if (response && response.success && response.enhancedImage) {
        handleCanvasLayerUpdate(selectedCanvasLayerId, {
          url: response.enhancedImage,
        });
      }
    } catch (error) {
      console.error('AI enhancement error:', error);
    } finally {
      dispatch({ type: HIDE_LOADING_MODAL });
      setInputDisabled(false);
    }
  }, [selectedCanvasLayerId, canvasLayers, handleCanvasLayerUpdate]);

  // Canvas AI image generation
  const handleCanvasAIGenerate = useCallback(async (prompt: string) => {
    try {
      if (!prompt || prompt.trim() === '') {
        return;
      }

      setInputDisabled(true);
      // Show global loading while generation is running
      dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Generating image...' } });

      const response = await apis.generateAIImage(prompt.trim());

      if (response && response.success && response.image) {
        const imageDataUrl = response.image; // Expecting data URL from backend

        // If there's a selected image layer, replace it; otherwise add a new layer
        if (selectedCanvasLayerId) {
          const selectedLayer = canvasLayers.find(l => l.id === selectedCanvasLayerId);
          if (selectedLayer && selectedLayer.type === 'image') {
            // Replace the selected image
            handleCanvasLayerUpdate(selectedCanvasLayerId, {
              url: imageDataUrl,
            });
          } else {
            // Selected layer is not an image, add a new one
            handleMediaSelect(imageDataUrl);
          }
        } else {
          // No selected layer, add a new centered image layer
          handleMediaSelect(imageDataUrl);
        }
      }
    } catch (error) {
      console.error('AI image generation error:', error);
      // Could show a toast/alert here if you have a notification system
    } finally {
      dispatch({ type: HIDE_LOADING_MODAL });
      setInputDisabled(false);
    }
  }, [selectedCanvasLayerId, canvasLayers, handleCanvasLayerUpdate, handleMediaSelect]);

  // Document handlers
  const handleCreateDocument = useCallback(
    createHandleCreateDocument({
      dispatch,
      setChatMessages,
      setActiveWindowComponent,
      setDocument,
      setDocumentCreationStage,
      setPendingLayout,
      globalUser,
    }),
    [dispatch, setChatMessages, setActiveWindowComponent, setDocument, setDocumentCreationStage, setPendingLayout, globalUser]
  );

  const handleDocumentLayoutApproval = useCallback(
    createHandleDocumentLayoutApproval({
      dispatch,
      setChatMessages,
      setDocumentCreationStage,
      setPendingLayout,
      setPendingContent,
      globalUser,
    }),
    [dispatch, setChatMessages, setDocumentCreationStage, setPendingLayout, setPendingContent, globalUser]
  );

  const handleDocumentContentApproval = useCallback(
    createHandleDocumentContentApproval({
      dispatch,
      setChatMessages,
      setActiveWindowComponent,
      setDocument,
      setDocumentCreationStage,
      setPendingContent,
      globalUser,
    }),
    [dispatch, setChatMessages, setActiveWindowComponent, setDocument, setDocumentCreationStage, setPendingContent, globalUser]
  );

  const handleDocumentDesignChange = useCallback(
    createHandleDocumentDesignChange({
      dispatch,
      setChatMessages,
      setActiveWindowComponent,
      setDocument,
      setDocumentCreationStage,
      globalUser,
    }),
    [dispatch, setChatMessages, setActiveWindowComponent, setDocument, setDocumentCreationStage, globalUser]
  );

  // Handle document element updates
  const handleDocumentElementUpdate = useCallback((elementId: string, updates: Partial<DocumentElement>) => {
    if (document && currentDocumentPage !== undefined) {
      const updatedPages = document.pages.map((page: any, index: number) => {
        if (index === currentDocumentPage && page.elements) {
          return {
            ...page,
            elements: page.elements.map((el: DocumentElement) =>
              el.element_id === elementId ? { ...el, ...updates } : el
            ),
          };
        }
        return page;
      });
      setDocument({ ...document, pages: updatedPages });
    }
  }, [document, currentDocumentPage]);

  const handleDocumentElementAdd = useCallback((element: Omit<DocumentElement, 'element_id'>) => {
    if (document && currentDocumentPage !== undefined) {
      const newElementId = uniqueId('element_');
      const newElement: DocumentElement = { ...element, element_id: newElementId };
      const updatedPages = document.pages.map((page: any, index: number) => {
        if (index === currentDocumentPage) {
          return {
            ...page,
            elements: [...(page.elements || []), newElement],
          };
        }
        return page;
      });
      setDocument({ ...document, pages: updatedPages });
      return newElementId;
    }
    return '';
  }, [document, currentDocumentPage]);

  const handleDocumentElementRemove = useCallback((elementId: string) => {
    if (document && currentDocumentPage !== undefined) {
      const updatedPages = document.pages.map((page: any, index: number) => {
        if (index === currentDocumentPage && page.elements) {
          return {
            ...page,
            elements: page.elements.filter((el: DocumentElement) => el.element_id !== elementId),
          };
        }
        return page;
      });
      setDocument({ ...document, pages: updatedPages });
      if (selectedDocumentElementId === elementId) {
        setSelectedDocumentElementId(null);
      }
    }
  }, [document, currentDocumentPage, selectedDocumentElementId]);

  // Handle metadata dialog submit - defined after canvas handlers
  const handleExecuteAction = useCallback(
    createHandleExecuteAction({
      dispatch,
      navigation,
      currentUserQuery,
      setExtractedFormData,
      setActionRequiringData,
      setPendingActions,
      actionRequiringData,
      setLastExecutedAction,
      setShowPostExecutionFeedback,
      timeoutIdsRef,
      activeWindowComponent,
      setActiveWindowComponent,
      handleCanvasAIGenerate,
      handleCanvasAIEnhance,
      selectedCanvasLayerId,
      canvasLayers,
      setChatMessages,
      handleCreateDocument,
    }),
    [dispatch, navigation, currentUserQuery, setExtractedFormData, setActionRequiringData, setPendingActions, actionRequiringData, setLastExecutedAction, setShowPostExecutionFeedback, timeoutIdsRef, activeWindowComponent, setActiveWindowComponent, handleCanvasAIGenerate, handleCanvasAIEnhance, selectedCanvasLayerId, canvasLayers, setChatMessages, handleCreateDocument]
  );
  // Handle action card click - inject form into chat
  const handleActionCardClick = useCallback(
    createHandleActionCardClick({
      dispatch,
      navigation,
      currentUserQuery,
      setExtractedFormData,
      setActionRequiringData,
      setPendingActions,
      actionRequiringData,
      setLastExecutedAction,
      setShowPostExecutionFeedback,
      timeoutIdsRef,
      activeWindowComponent,
      setActiveWindowComponent,
      handleCanvasAIGenerate,
      handleCanvasAIEnhance,
      selectedCanvasLayerId,
      canvasLayers,
      setChatMessages,
      handleExecuteAction,
    }),
    [dispatch, navigation, currentUserQuery, setExtractedFormData, setActionRequiringData, setPendingActions, actionRequiringData, setLastExecutedAction, setShowPostExecutionFeedback, timeoutIdsRef, activeWindowComponent, setActiveWindowComponent, handleCanvasAIGenerate, handleCanvasAIEnhance, selectedCanvasLayerId, canvasLayers, setChatMessages, handleExecuteAction]
  );

  // Handle data form submit - defined after handleExecuteAction
  const handleDataFormSubmit = useCallback((template: ActionTemplate, data: any) => {
    handleExecuteAction(template, data);
  }, [handleExecuteAction]);

  // Handle form submission - defined after handleExecuteAction
  const handleFormSubmit = useCallback(async (values, messageId) => {
    // Find the message to get the action template and check if it's onboarding
    const formMessage = chatMessages.find(msg => msg._id === messageId);

    if (!formMessage) {
      console.error("❌ ERROR: Form message not found with ID:", messageId);
      console.log("Available message IDs:", chatMessages.map(msg => ({ id: msg._id, type: msg.showProfileForm ? 'profile' : msg.showOnboardingForm ? 'onboarding' : 'other' })));
    }

    const actionTemplate = formMessage?.actionTemplate;
    const sourcePage = formMessage?.actionSourcePage || 'EasyModeScreen';
    // Check if this is a profile form (Stage 1) or onboarding form (Stage 2)
    const isProfileForm = formMessage?.showProfileForm === true;
    const isOnboardingForm = formMessage?.showOnboardingForm === true;

    // Disable the form after submission
    setChatMessages(prev => prev.map(msg => {
      if (msg._id === messageId) {
        return {
          ...msg,
          formSubmitted: true,
          formValues: values
        };
      }
      return msg;
    }));

    // Handle profile form submission (Stage 1 - MUST BE COMPLETED FIRST)
    if (isProfileForm) {
      try {
        // Validate required profile fields before submission
        if (!values.name || !values.surname) {
          throw new Error('Name and surname are required');
        }

        // Save profile data to user profile
        const response = await apis.updateUserProfile(values);

        if (response && response.success) {
          // Update Redux store with updated user data
          // The API returns { success: true, data: { changes: { field: { old, new } } } }
          const responseData = (response as any).data;
          if (responseData?.changes) {
            // Extract 'new' values from changes object structure: { field: { old: x, new: y } }
            const updatedUserData: Record<string, any> = {};
            Object.keys(responseData.changes).forEach(key => {
              const change = responseData.changes[key];
              // If change has 'new' property, extract it; otherwise use the change itself
              updatedUserData[key] = change?.new !== undefined ? change.new : change;
            });

            dispatch({
              type: UPDATE_USER,
              payload: updatedUserData
            });
          } else if (responseData) {
            // If response.data contains user data directly (not in changes format)
            dispatch({
              type: UPDATE_USER,
              payload: responseData
            });
          } else {
            // Fallback: Update Redux with the values we submitted
            dispatch({
              type: UPDATE_USER,
              payload: values
            });
          }

          // Mark profile form as completed - this allows business form to be shown
          setProfileFormCompleted(true);

          // Store profile data for context stage
          setSavedProfileData(values);

          // Add success message and transition to context stage
          const successMessageId = uniqueId();
          setChatMessages(prevMessages =>
            GiftedChat.append(prevMessages, [{
              _id: successMessageId,
              text: "Great! Now let's learn more about your business and preferences.",
              createdAt: new Date(),
              system: true,
              received: true
            }])
          );

          // Second onboarding stage: Business user details
          // SAFEGUARD: Only inject business form AFTER profile is marked as completed
          // Inject context/business information form (Stage 2 - ONLY AFTER PROFILE COMPLETION)
          setTimeout(() => {
            // Profile form is completed at this point, proceed with business form injection
            const contextMessageId = uniqueId();
            setChatMessages(prevMessages =>
              GiftedChat.append(prevMessages, [{
                _id: contextMessageId,
                text: "Please fill out the following information about your business and preferences:",
                createdAt: new Date(),
                system: true,
                showProfileForm: false,
                showOnboardingForm: true, // Flag for context/business stage
                formSchema: onboardSchema,
                formSubmitted: false,
                formValues: {},
                received: true
              }])
            );
            setOnboardingStage('context');
          }, 500);
        } else {
          throw new Error('Failed to update profile');
        }
      } catch (error) {
        console.error('❌ [EasyModeScreen] Error submitting profile form:', error);

        // Profile form failed - do NOT mark as completed
        setProfileFormCompleted(false);

        // Add error message to chat
        const errorMessageId = uniqueId();
        setChatMessages(prevMessages =>
          GiftedChat.append(prevMessages, [{
            _id: errorMessageId,
            text: "Sorry, there was an error saving your profile information. Please try again.",
            createdAt: new Date(),
            system: true,
            received: true
          }])
        );

        // Re-enable the form so user can retry
        setChatMessages(prev => prev.map(msg => {
          if (msg._id === messageId) {
            return {
              ...msg,
              formSubmitted: false
            };
          }
          return msg;
        }));
      }
      return;
    }

    // Handle context/business form submission (Stage 2 - ONLY AFTER PROFILE COMPLETION)
    if (isOnboardingForm) {
      try {
        await handleBusinessFormSubmit(values, {
          dispatch,
          setChatMessages,
          setOnboardingStage,
          setSavedProfileData,
          setProfileFormCompleted,
          profileFormCompleted,
          savedProfileData,
          sendMessageSequentially: async (messages: any[], delay = 1000) => {
            for (let i = 0; i < messages.length; i++) {
              await new Promise(resolve => setTimeout(resolve, delay));
              setChatMessages(prevMessages => GiftedChat.append(prevMessages, [messages[i]]));
            }
          },
          globalUser,
          userData,
        });

        // If no models were found (onboardingStage is null), fetch integration suggestions
        // Check after a short delay to allow state updates
        setTimeout(() => {
          if (onboardingStage === null) {
            fetchIntegrationSuggestions();
          }
        }, 1000);
      } catch (error) {
        console.error('❌ [EasyModeScreen] Error in business form submission:', error);
        // Re-enable the form so user can retry
        setChatMessages(prev => prev.map(msg => {
          if (msg._id === messageId) {
            return {
              ...msg,
              formSubmitted: false
            };
          }
          return msg;
        }));
      }
      return;
    }

    // If there's an action template, execute it with the form data
    if (actionTemplate) {
      // Execute the action with form data and source page for badge increment
      handleExecuteAction(actionTemplate, values, sourcePage);
      // Success message removed - action execution is sufficient feedback
    } else {
      // Fallback: Add user response to chat (for non-action forms)
      const responseText = Object.entries(values)
        .map(([key, value]) => `${key}: ${value}`)
        .join('\n');

      handleQuery(responseText);
    }
  }, [chatMessages, handleExecuteAction, dispatch, profileFormCompleted, savedProfileData, handleFormSkip, setChatMessages, setProfileFormCompleted, setSavedProfileData, setOnboardingStage, fetchIntegrationSuggestions, handleQuery]);

  // Canvas export
  const handleCanvasExport = useCallback(async () => {
    if (!canvas.canvasRef.current) return;
    try {
      const imageData = await canvas.canvasRef.current.exportCanvas();
      // Pass to PostToFeed - we'll need to trigger it somehow
      console.log('Canvas exported:', imageData.substring(0, 50));
      // TODO: Integrate with PostToFeed
    } catch (error) {
      console.error('Canvas export error:', error);
    }
  }, []);

  // Canvas save to media
  const handleCanvasSaveToMedia = useCallback(async () => {
    if (!canvas.canvasRef.current) return;
    try {
      dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Exporting canvas...' } });

      // Export canvas as data URL
      const imageData = await canvas.canvasRef.current.exportCanvas();

      // Create file name with timestamp
      const fileName = `canvas-${Date.now()}.png`;

      // Prepare media payload
      // Backend expects data URL or base64 string - it will handle conversion
      const mediaPayload = {
        name: fileName,
        data: imageData, // Pass data URL directly - backend will handle conversion
        type: 'image/png',
        metadata: {
          uploadedVia: 'canvas',
          source: 'imageCanvas',
          createdAt: new Date().toISOString(),
        },
      };

      // Upload to media library (automatically uses user or company context from session)
      const result = await apis.uploadGlobalAsset(mediaPayload);

      dispatch({ type: HIDE_LOADING_MODAL });

      if (result && Array.isArray(result) && result.length > 0) {
        Alert.alert('Success', 'Canvas has been saved to your media library!');
      } else if (result && typeof result === 'string') {
        Alert.alert('Success', 'Canvas has been saved to your media library!');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error: any) {
      console.error('Error saving canvas to media:', error);
      dispatch({ type: HIDE_LOADING_MODAL });
      Alert.alert(
        'Save Failed',
        error.message || 'Failed to save canvas to media library'
      );
    }
  }, [dispatch]);

  // Editor save handler
  const handleEditorSave = useCallback(async (content: string) => {
    try {
      editorContextHook.setContent(content);
      console.log('Editor content saved:', content.substring(0, 50));
      // TODO: Add API call to save editor content if needed
    } catch (error) {
      console.error('Editor save error:', error);
    }
  }, [editorContextHook]);

  // Send message to editor - using utility function
  const handleSendToEditor = useCallback(
    async (messageText: string, format: 'plain' | 'html' = 'html') => {
      console.log('📝 [EasyModeScreen] handleSendToEditor called', {
        messageLength: messageText?.length || 0,
        format,
        platform: Platform.OS,
        showEditor,
        isEditorConnected,
        hasEditorContext: !!editorContext,
        hasEditorRef: !!editorContext?.ref,
        editorRefCurrent: !!editorContext?.ref?.current,
        activeWindowComponent,
        editorContextKeys: editorContext ? Object.keys(editorContext) : [],
      });

      // Auto-open editor window if not already open and editor ref doesn't exist
      const needsToOpenEditor = (!showEditor || !editorContext?.ref?.current) && Platform.OS === 'web';
      console.log('🔍 [EasyModeScreen] Editor state check', {
        needsToOpenEditor,
        showEditor,
        hasRef: !!editorContext?.ref,
        hasRefCurrent: !!editorContext?.ref?.current,
        platform: Platform.OS,
      });

      if (needsToOpenEditor) {
        console.log('🚀 [EasyModeScreen] Opening editor window...');
        setActiveWindowComponent('editor');

        // Wait for editor to mount and context to be set up (with retries)
        let attempts = 0;
        const maxAttempts = 10;
        const delayMs = 200;

        while (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, delayMs));
          attempts++;

          // Re-check editorContext from the hook (it might have updated)
          const currentEditorContext = editorContextHook.editorContext;
          const hasRef = !!currentEditorContext?.ref;
          const hasRefCurrent = !!currentEditorContext?.ref?.current;

          console.log(`⏳ [EasyModeScreen] Waiting for editor context (attempt ${attempts}/${maxAttempts})...`, {
            hasEditorContext: !!currentEditorContext,
            hasRef,
            hasRefCurrent,
          });

          if (hasRef && hasRefCurrent) {
            console.log('✅ [EasyModeScreen] Editor context is ready!');
            break;
          }
        }

        if (attempts >= maxAttempts) {
          console.warn('⚠️ [EasyModeScreen] Editor context not ready after max attempts, proceeding anyway...');
        }
      }

      // Get fresh editor context after waiting
      const currentEditorContext = editorContextHook.editorContext;
      const editorRef = currentEditorContext?.ref || editorContext?.ref || { current: null };

      console.log('📋 [EasyModeScreen] Creating handler with editor ref', {
        hasRef: !!editorRef,
        hasRefCurrent: !!editorRef.current,
        refType: typeof editorRef,
        refKeys: editorRef ? Object.keys(editorRef) : [],
        usingCurrentContext: !!currentEditorContext,
      });

      try {
        const handler = createHandleSendToEditor(
          editorRef,
          (content: string) => {
            console.log('💾 [EasyModeScreen] setEditorContent called', {
              contentLength: content?.length || 0,
              hasEditorContextHook: !!editorContextHook,
            });
            editorContextHook.setContent(content);
          }
        );

        console.log('✅ [EasyModeScreen] Handler created, calling with message', {
          messageLength: messageText?.length || 0,
          format,
        });
        handler(messageText, format);
        console.log('✅ [EasyModeScreen] Handler call completed');
      } catch (error) {
        console.error('❌ [EasyModeScreen] Error in handleSendToEditor', {
          error,
          errorMessage: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
        });
        throw error;
      }
    },
    [editorContext, editorContextHook, showEditor, isEditorConnected, setActiveWindowComponent, activeWindowComponent]
  );

  // Send conversation summary to editor - using utility function
  const handleSendSummaryToEditor = useCallback(
    async () => {
      // Auto-open editor window if not already open and editor ref doesn't exist
      if ((!showEditor || !editorContext?.ref?.current) && Platform.OS === 'web') {
        setActiveWindowComponent('editor');
        // Wait a bit for the editor to mount
        await new Promise(resolve => setTimeout(resolve, 400));
      }

      const handler = createHandleSendSummaryToEditor({
        showEditor: true, // Editor should be open now
        editorRef: editorContext?.ref || { current: null },
        chatMessages,
        globalUser,
        isEditorConnected,
        setInputDisabled,
        handleSendToEditor,
        theme,
      });
      await handler();
    },
    [showEditor, editorContext, chatMessages, globalUser, isEditorConnected, setInputDisabled, handleSendToEditor, theme, setActiveWindowComponent]
  );

  // Send entire conversation to editor - using utility function
  const handleSendConversationToEditor = useCallback(
    async () => {
      // Auto-open editor window if not already open and editor ref doesn't exist
      if ((!showEditor || !editorContext?.ref?.current) && Platform.OS === 'web') {
        setActiveWindowComponent('editor');
        // Wait a bit for the editor to mount
        await new Promise(resolve => setTimeout(resolve, 400));
      }

      const handler = createHandleSendConversationToEditor({
        showEditor: true, // Editor should be open now
        editorRef: editorContext?.ref || { current: null },
        chatMessages,
        globalUser,
        theme,
        handleSendToEditor,
        isEditorConnected: false,
        setInputDisabled: function (disabled: boolean): void {
          throw new Error('Function not implemented.');
        }
      });
      handler();
    },
    [showEditor, editorContext, chatMessages, globalUser, theme, handleSendToEditor, setActiveWindowComponent]
  );

  // Update refs when callbacks change - must be after all handlers are declared
  useEffect(() => {
    handleQueryRef.current = handleQuery;
    handleModelPressRef.current = handleModelPress;
    handleManualEditRef.current = handleManualEdit;
    handleDragOverRef.current = handleDragOver;
    handleDragLeaveRef.current = handleDragLeave;
    handleDropRef.current = handleDrop;
    handleDataFileProcessedRef.current = handleDataFileProcessed;
    handleToggleHierarchyRef.current = handleToggleHierarchy;
    handleToggleCanvasRef.current = handleToggleCanvas;
    handleToggleEditorRef.current = handleToggleEditor;
    handleToggleDataHandlerRef.current = handleToggleDataHandler;
    fetchIntegrationSuggestionsRef.current = fetchIntegrationSuggestions;
  }, [
    handleQuery,
    handleModelPress,
    handleManualEdit,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDataFileProcessed,
    handleToggleHierarchy,
    handleToggleCanvas,
    handleToggleEditor,
    handleToggleDataHandler,
    fetchIntegrationSuggestions,
  ]);

  return (
    // KeyboardAvoidingView is disabled on web - web handles keyboard via CSS/viewport
    // On native, we disable KeyboardAvoidingView since input is absolutely positioned
    // This prevents the entire screen from shifting when keyboard appears
    <View
      style={[styles.container, {
        paddingHorizontal: activeWindowComponent ? 0 : (focusMode ? 0 : Platform.select({ web: "3%", native: undefined })),
        paddingRight: focusMode ? 0 : undefined,
        paddingBottom: Platform.select({ native: 200, web: 10 }),
      }]}>

      {/* Gradient Background */}
      <LinearGradient
        colors={theme
          ? ['#FEFFFF', '#E6F9FF', '#F7E9FF', '#FFFFFF']
          : ['#1a1a1a', '#1e293b', '#1e1b2e', '#1a1a1a']
        }
        locations={[0, 0.3, 0.7, 1]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={StyleSheet.absoluteFill}
      />

      {/* Screen Burn Overlay Effect */}
      <View
        style={{
          ...StyleSheet.absoluteFillObject,
          backgroundColor: theme
            ? 'rgba(230, 105, 105, 0.02)'
            : 'rgba(59, 130, 246, 0.03)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />





      {/* Subtle noise/grain texture overlay (web only) */}
      {Platform.OS === 'web' && (
        <View
          style={{
            ...StyleSheet.absoluteFillObject,
            pointerEvents: 'none',
            zIndex: 0,
            opacity: 0.03,
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'repeat',
          } as any}
        />
      )}

      {/* Split View Container */}
      <View style={{ flex: 1, flexDirection: 'row', zIndex: 1 }}>
        {/* Chat Area - Left Side */}
        <YStack width="50%" minWidth={400} flex={activeWindowComponent ? 0.55 : 1}>
          {/* Selected Agent Display - Top Left (outside input toolbar) */}
          {selectedAgent && (
            <View style={{
              position: 'absolute',
              paddingTop: "5%",
              top: Platform.select({ web: 20, native: 50 }),
              left: Platform.select({ web: 20, native: 20 }),
              zIndex: 2000,
              flexDirection: 'row',
              alignItems: 'flex-start',
              gap: 12,
            }}>
              <View style={{
                backgroundColor: theme ? '#ffffff' : '#1e293b',
                borderRadius: 12,
                padding: 12,
                minWidth: 120,
                borderWidth: 2,
                borderColor: theme ? '#3b82f6' : '#60a5fa',
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.2,
                shadowRadius: 8,
                elevation: 5,
              }}>
                <YStack gap={8} alignItems="center">
                  <View style={{
                    width: 80,
                    height: 80,
                    borderRadius: 20,
                    backgroundColor: theme ? '#f0f9ff' : '#1e3a5f',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <Image
                      source={getAgentAvatarSource(selectedAgent)}
                      style={{
                        width: 60,
                        height: 60,
                        borderRadius: 20,
                      }}
                      resizeMode="cover"
                    />
                    <View style={{
                      position: 'absolute',
                      top: -2,
                      right: -2,
                      width: 12,
                      height: 12,
                      borderRadius: 6,
                      backgroundColor: selectedAgent.status === 'active' ? '#10b981' : selectedAgent.status === 'draft' ? '#f59e0b' : '#6b7280',
                      borderWidth: 2,
                      borderColor: theme ? '#ffffff' : '#1e293b'
                    }} />
                  </View>
                  <YStack flex={1} gap={2}>
                    <TamaguiText style={{
                      color: theme ? '#1e293b' : '#f1f5f9',
                      fontSize: 12,
                      textAlign: "center",
                      fontWeight: '600'
                    }} numberOfLines={1}>
                      {selectedAgent.name}
                    </TamaguiText>
                    <TamaguiText style={{
                      color: theme ? '#64748b' : '#94a3b8',
                      fontSize: 10
                    }} numberOfLines={1}>
                      {selectedAgent.type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      {selectedAgent.status === 'draft' && ' • Draft'}
                    </TamaguiText>
                  </YStack>
                  <Pressable
                    onPress={() => {
                      console.log('Closing selected agent');
                      setSelectedAgent(null);
                    }}
                    style={{
                      marginTop: 4,
                      padding: 4,
                    }}
                  >
                    <AntDesign name="close" size={16} color={theme ? '#64748b' : '#94a3b8'} />
                  </Pressable>
                </YStack>
              </View>
              {/* Vertical Separator */}
              <View style={{
                width: 2,
                height: 200,
                backgroundColor: theme ? '#e2e8f0' : '#334155',
                marginLeft: 8,
                alignSelf: 'flex-start',
              }} />
              {/* Agent Action History Placeholder */}
              <View style={{
                flex: 1,
                backgroundColor: theme ? 'rgba(255, 255, 255, 0.9)' : 'rgba(30, 41, 59, 0.9)',
                borderRadius: 12,
                padding: 16,
                minWidth: 300,
                maxWidth: 400,
                borderWidth: 1,
                borderColor: theme ? '#e2e8f0' : '#334155',
                ...(Platform.OS === 'web' && {
                  backdropFilter: 'blur(10px)',
                })
              }}>
                <TamaguiText style={{
                  color: theme ? '#1e293b' : '#f1f5f9',
                  fontSize: 14,
                  fontWeight: '600',
                  marginBottom: 12
                }}>
                  Agent Action History
                </TamaguiText>
                <View style={{
                  padding: 12,
                  backgroundColor: theme ? '#f8f9fa' : '#1e293b',
                  borderRadius: 8,
                  borderWidth: 1,
                  borderColor: theme ? '#e2e8f0' : '#334155',
                  borderStyle: 'dashed',
                }}>
                  <TamaguiText style={{
                    color: theme ? '#64748b' : '#94a3b8',
                    fontSize: 12,
                    textAlign: 'center',
                    fontStyle: 'italic'
                  }}>
                    Action history will appear here...
                  </TamaguiText>
                </View>
              </View>
            </View>
          )}
          <View style={[styles.chatContainer, {
            flex: 1,
            marginRight: focusMode ? 0 : Platform.select({ web: undefined, native: 30 })
          }]}>
            <GiftedChat
              isDayAnimationEnabled={false}
              messages={chatMessages}
              key="easyModeChat"
              textInputRef={inputRef}
              isUserAvatarVisible
              messagesContainerStyle={{
                paddingBottom: Platform.OS === 'web' ? '30dvh' : "60%",
                paddingTop: Platform.select({ native: "22%", web: "10dvh" }),
                maxHeight: Platform.select({ web: "100%", native: "90%" }),
                top: Platform.select({ web: -30, native: -30 })
              } as any}
              minInputToolbarHeight={50}
              listProps={{
                ref: scrollViewRef,
                nestedScrollEnabled: true,
                scrollEnabled: true,
                showsVerticalScrollIndicator: false,
                keyboardShouldPersistTaps: 'always', // Changed to 'always' to prevent focus loss
                keyboardDismissMode: 'none', // Changed to 'none' to prevent keyboard dismissal on scroll
                onScroll: (e: any) => {
                  // Prevent scrolling when keyboard just appeared (causes focus loss)
                  // Note: We can't actually prevent the scroll here, but we can track it
                  if (preventScrollOnKeyboardRef.current) {
                    // Scroll happened during keyboard appearance - this might cause blur
                    // The blur prevention in InputToolbar should handle this
                  }
                  // Capture scroll events to maintain proper scrolling behavior
                  // This allows the parent FlatList to scroll while nested ScrollViews can also scroll
                },
                // Important: These props help ensure nested ScrollViews can capture scroll events
                removeClippedSubviews: false, // Prevents clipping issues with nested scrollables
                // Prevent ScrollView from interfering with input focus
                scrollEventThrottle: 16,
                // Prevent ScrollView from scrolling when keyboard appears (causes focus loss)
                onContentSizeChange: (contentWidth: number, contentHeight: number) => {
                 
                  // Prevent auto-scroll when content size changes (e.g., when keyboard appears)
                  // This prevents the ScrollView from scrolling and causing input blur
                  // If keyboard just appeared, prevent scrolling to avoid focus loss
                  if (preventScrollOnKeyboardRef.current) {
                    // Don't allow ScrollView to scroll when keyboard is appearing
                    // This prevents the content size change from triggering a scroll that causes blur
                    // IMMEDIATELY restore focus to prevent keyboard from dismissing
                    if (inputRef.current) {
                      console.log("🚫 [EasyModeScreen] Attempting immediate focus restoration");
                      // Multiple immediate attempts to restore focus
                      try {
                        inputRef.current.focus(); // Synchronous attempt
                        console.log("🚫 [EasyModeScreen] Synchronous focus attempt completed");
                      } catch (e) {
                        console.warn("🚫 [EasyModeScreen] Synchronous focus failed:", e);
                      }
                      setTimeout(() => {
                        inputRef.current?.focus();
                        console.log("🚫 [EasyModeScreen] 0ms focus attempt completed");
                      }, 0); // Next tick
                      setTimeout(() => {
                        inputRef.current?.focus();
                        console.log("🚫 [EasyModeScreen] 10ms focus attempt completed");
                      }, 10); // Very short delay
                    } else {
                      console.warn("⚠️ [EasyModeScreen] Input ref is null in onContentSizeChange");
                    }
                    return;
                  }
                },
                onLayout: () => {
                  // Prevent layout changes from triggering scroll that causes blur
                  // If keyboard just appeared, don't allow layout changes to trigger scroll
                  if (preventScrollOnKeyboardRef.current) {
                    return;
                  }
                },
              } as any}
              renderInputToolbar={useCallback(() => {
                // Hide entire input toolbar when agent is selected
                if (selectedAgent) {
                  return null;
                }

                // Use memoized hasMessages - already calculated above
                // Memoize styles to prevent recalculation
                const outerContainerStyle: any = {
                  position: 'absolute' as const,
                  left: 0,
                  right: 0,
                  ...(hasMessages ? {
                    bottom: Platform.select({ native: 90, web: 0 }),
                    top: undefined,
                  } : {
                    top: 0,
                    bottom: 40,
                    justifyContent: 'center' as const,
                    alignItems: 'center' as const,
                  }),
                  // Ensure toolbar stays visible during streaming
                  zIndex: 1000,
                  elevation: Platform.OS === 'android' ? 10 : undefined,
                };

                const innerContainerStyle: any = [
                  styles.inputContainer,
                  {
                    paddingHorizontal: Platform.select({ web: "0%", native: "0%" }),
                    backgroundColor: "transparent",
                    minHeight: Platform.select({ web: "15%", default: "20%" }),
                    borderTopWidth: 0,
                    // Ensure container stays visible during streaming
                    zIndex: 1001,
                    elevation: Platform.OS === 'android' ? 11 : undefined,
                    ...(hasMessages ? {
                      position: 'absolute' as const,
                      bottom: Platform.select({ web: 0, native: 80 }),
                      marginHorizontal: Platform.select({ web: 30, native: undefined }),
                      width: Platform.select({ web: "96%", native: "120%" }),
                      paddingRight: Platform.select({ web: 20 })
                    } : {
                        position: 'relative' as const,
                        width: Platform.select({ web: "98%", native: "105%" }),
                      alignSelf: 'flex-start' as const,
                        left: Platform.select({ native: "-6%" }),
                    }),
                  }
                ];

                return (
                  <View style={outerContainerStyle}>
                    {/* Form Action Buttons - Show above toolbar when form is active */}
                    {activeFormMessage && activeFormMessageRef.current && (
                      <Animatable.View animation={'slideInUp'} style={{
                        paddingHorizontal: Platform.select({ web: 30, native: 20 }),
                        paddingBottom: 12,
                        paddingTop: Platform.select({ web: 0, native: 0 }),
                        width: '100%',
                        bottom: "25dvh",
                        backgroundColor: 'transparent',
                      }}>
                        <XStack
                          transform={[{ scale: 0.85 }]}
                          left={Platform.select({ web: -5, native: -10 })}
                          minWidth="100%"
                          height="100%"
                          alignSelf="stretch"
                          gap={10}
                        >
                          <ActionArray
                            conjoined={true}
                            wrap
                            activeComponent="skip"
                            actionArray={[{
                              functionName: "skip",
                              function: () => {
                                activeFormMessageRef.current?.skip();
                              },
                              text: "Skip",
                              icon: "step-forward",
                            },
                            {
                              functionName: "reset",
                              function: () => {
                                activeFormMessageRef.current?.reset();
                              },
                              text: "Reset",
                              icon: "redo",
                            },
                            {
                              functionName: "submit",
                              function: () => {
                                activeFormMessageRef.current?.submit();
                              },
                              text: "Submit",
                              icon: "play-circle",
                            }
                            ]} />
                        </XStack>
                      </Animatable.View>
                    )}

                    {/* Active Agents Display - Only show when chat hasn't started and no agent is selected */}
                    {!hasMessages && !selectedAgent && (
                      <View style={{
                        paddingHorizontal: Platform.select({ web: 30, native: 20 }),
                        paddingBottom: 16,
                        width: '100%',
                      }}>
                        {activeAgents.length > 0 ? (
                          <YStack gap={12}>
                            <TamaguiText style={{
                              color: theme ? '#64748b' : '#94a3b8',
                              fontSize: 14,
                              fontWeight: '600',
                              marginBottom: 8
                            }}>
                              Active Agents
                            </TamaguiText>
                            <ScrollView
                              horizontal
                              showsHorizontalScrollIndicator={false}
                              contentContainerStyle={{ gap: 12, paddingRight: 20 }}
                              scrollEnabled={true}
                              nestedScrollEnabled={true}
                              {...(Platform.OS === 'web' && {
                                // @ts-ignore - prevent scroll from interfering with clicks
                                onMouseDown: undefined,
                              })}
                            >
                              {activeAgents.filter((agent: any) => agent._id !== selectedAgent?._id).map((agent: any) => {
                                const avatarSource = getAgentAvatarSource(agent);
                                const handleAgentClick = () => {
                                  console.log('Agent clicked:', agent.name, agent._id);
                                  console.log('Current selectedAgent before update:', selectedAgent);
                                  setSelectedAgent((prev) => {
                                    console.log('Setting selectedAgent to:', agent.name);
                                    return agent;
                                  });
                                  // Force a re-render check
                                  setTimeout(() => {
                                    console.log('SelectedAgent after update (check):', agent.name);
                                  }, 100);
                                };

                                return (
                                  <TouchableOpacity
                                    key={agent._id}
                                    onPress={handleAgentClick}
                                    activeOpacity={0.7}
                                    hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                                    style={{
                                      backgroundColor: theme ? '#ffffff' : '#1e293b',
                                      borderRadius: 12,
                                      padding: 12,
                                      minWidth: 120,
                                      borderWidth: 2,
                                      borderColor: theme ? '#e2e8f0' : '#334155',
                                      shadowColor: '#000',
                                      shadowOffset: { width: 0, height: 2 },
                                      shadowOpacity: 0.1,
                                      shadowRadius: 4,
                                      elevation: 3,
                                      cursor: Platform.OS === 'web' ? 'pointer' : undefined,
                                    }}
                                  >
                                    <YStack gap={8} alignItems="center" pointerEvents="box-none">
                                      <View style={{
                                        width: 120,
                                        height: 120,
                                        borderRadius: 20,
                                        backgroundColor: theme ? '#f0f9ff' : '#1e3a5f',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        position: 'relative',
                                        overflow: 'hidden'
                                      }}>
                                        <Image
                                          source={avatarSource}
                                          style={{
                                            width: 80,
                                            height: 80,
                                            borderRadius: 20,
                                          }}
                                          resizeMode="cover"
                                        />
                                        {/* Status badge */}
                                        <View style={{
                                          position: 'absolute',
                                          top: -2,
                                          right: -2,
                                          width: 12,
                                          height: 12,
                                          borderRadius: 6,
                                          backgroundColor: agent.status === 'active' ? '#10b981' : agent.status === 'draft' ? '#f59e0b' : '#6b7280',
                                          borderWidth: 2,
                                          borderColor: theme ? '#ffffff' : '#1e293b'
                                        }} />
                                      </View>
                                      <YStack flex={1} gap={2}>
                                        <TamaguiText style={{
                                          color: theme ? '#1e293b' : '#f1f5f9',
                                          fontSize: 12,
                                          textAlign: "center",
                                          fontWeight: '600'
                                        }} numberOfLines={1}>
                                          {agent.name}
                                        </TamaguiText>
                                        <TamaguiText style={{
                                          color: theme ? '#64748b' : '#94a3b8',
                                          fontSize: 10
                                        }} numberOfLines={1}>
                                          {agent.type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                          {agent.status === 'draft' && ' • Draft'}
                                        </TamaguiText>
                                      </YStack>
                                    </YStack>
                                  </TouchableOpacity>
                                );
                              })}
                            </ScrollView>
                          </YStack>
                        ) : (
                          <View style={{
                            backgroundColor: theme ? 'rgba(255, 255, 255, 0.7)' : 'rgba(30, 41, 59, 0.7)',
                            borderRadius: 16,
                            padding: 20,
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderWidth: 2,
                            borderColor: theme ? '#e2e8f0' : '#334155',
                            borderStyle: 'dashed',
                            ...(Platform.OS === 'web' && {
                              backdropFilter: 'blur(10px)',
                            })
                          }}>
                            <MaterialIcons
                              name="smart-toy"
                              size={48}
                              color={theme ? '#94a3b8' : '#64748b'}
                              style={{ marginBottom: 12 }}
                            />
                            <TamaguiText style={{
                              color: theme ? '#64748b' : '#94a3b8',
                              fontSize: 16,
                              fontWeight: '600',
                              marginBottom: 8,
                              textAlign: 'center'
                            }}>
                              No Active Agents
                            </TamaguiText>
                            <TamaguiText style={{
                              color: theme ? '#94a3b8' : '#64748b',
                              fontSize: 12,
                              textAlign: 'center',
                              marginBottom: 16
                            }}>
                              Create an autonomous agent to help automate your workflows
                            </TamaguiText>
                            <TamaguiButton
                              size="$4"
                              onPress={() => agentBuilderModalRef.current?.openModal()}
                              style={{
                                backgroundColor: theme ? '#3b82f6' : '#2563eb',
                              }}
                            >
                              <MaterialIcons name="add" size={16} color="white" style={{ marginRight: 6 }} />
                              <TamaguiText style={{ color: 'white', fontSize: 14, fontWeight: '600' }}>
                                Create Agent
                              </TamaguiText>
                            </TamaguiButton>
                          </View>
                        )}
                      </View>
                    )}
                    <View style={innerContainerStyle}>
                      <View
                        style={{ paddingLeft: "2%", flexDirection: 'row', alignItems: 'center', gap: 10, width: '100%' }}>
                        {/* Image Preview (Left Side) */}
                        {pendingImageAnalysis && (
                          <View style={{
                            height: "100%",
                            minWidth: "15%",
                            width: "15%",
                            maxWidth: "15%",
                            borderRadius: 8,
                            overflow: 'hidden',
                            backgroundColor: theme ? '#f0f0f0' : '#2a2a2a',
                            borderWidth: 2,
                            borderColor: '#3b82f6',
                            position: 'relative',
                            marginRight: 8,
                          }}>
                            <Image
                              source={{ uri: pendingImageAnalysis.imageData }}
                              style={{ width: '100%', height: '100%' }}
                              resizeMode="contain"
                            />
                            <Pressable
                              onPress={() => setPendingImageAnalysis(null)}
                              style={{
                                position: 'absolute',
                                top: -4,
                                right: -4,
                                backgroundColor: '#ef4444',
                                borderRadius: 10,
                                width: 20,
                                height: 20,
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 10,
                              }}
                            >
                              <AntDesign name="close" size={12} color="white" />
                            </Pressable>
                          </View>
                        )}
                        <InputWrapper
                          onDragOver={handleDragOverRef.current || handleDragOver}
                          onDragLeave={handleDragLeaveRef.current || handleDragLeave}
                          onDrop={handleDropRef.current || handleDrop}
                          draggedOver={draggedOver}
                          theme={theme}
                          onVoiceButtonPress={handleVoiceButtonPress}
                          isRecording={isRecording}
                          inputDisabled={inputDisabled}
                          onboardingInProgress={onboardingInProgress}
                          clearConversationHistory={clearConversationHistory}
                          setChatMessages={setChatMessages}
                          actionButtons={[
                            ...(__DEV__ ? [
                              {
                                icon: 'rocket',
                                onPress: () => dispatch({ type: START_ONBOARDING }),
                                backgroundColor: theme ? '#ffe0b2' : '#ff9800',
                                iconColor: theme ? '#e65100' : '#fff3e0',
                              },
                              {
                                icon: 'link',
                                onPress: () => {
                                  console.log('🧪 [EasyModeScreen] Testing integration suggestions...');
                                  if (fetchIntegrationSuggestionsRef.current) {
                                    fetchIntegrationSuggestionsRef.current();
                                  } else {
                                    fetchIntegrationSuggestions();
                                  }
                                },
                                backgroundColor: theme ? '#d1fae5' : '#10b981',
                                iconColor: theme ? '#065f46' : '#a7f3d0',
                              },
                            ] : []),
                            {
                              icon: showHierarchy ? 'close' : 'appstore',
                              onPress: handleToggleHierarchyRef.current || handleToggleHierarchy,
                              disabled: Platform.OS === 'web' && onboardingInProgress,
                              backgroundColor: theme ? '#ffffff' : '#1a1a1a',
                              iconColor: theme ? '#3b82f6' : '#60a5fa',
                            },
                            {
                              icon: showCanvas ? 'close' : 'picture',
                              onPress: handleToggleCanvasRef.current || handleToggleCanvas,
                              disabled: Platform.OS === 'web' && onboardingInProgress,
                              backgroundColor: showCanvas ? (theme ? '#10b981' : '#059669') : (theme ? '#ffffff' : '#1a1a1a'),
                              iconColor: showCanvas ? 'white' : (theme ? '#10b981' : '#34d399'),
                            },
                            {
                              icon: showEditor ? 'close' : 'file-text',
                              onPress: handleToggleEditorRef.current || handleToggleEditor,
                              disabled: Platform.OS === 'web' && onboardingInProgress,
                              backgroundColor: showEditor ? (theme ? '#7c3aed' : '#6d28d9') : (theme ? '#ffffff' : '#1a1a1a'),
                              iconColor: showEditor ? 'white' : (theme ? '#7c3aed' : '#a78bfa'),
                            },
                            {
                              icon: showDataHandler ? 'close' : 'database',
                              onPress: handleToggleDataHandlerRef.current || handleToggleDataHandler,
                              disabled: Platform.OS === 'web' && onboardingInProgress,
                              backgroundColor: showDataHandler ? (theme ? '#06b6d4' : '#0891b2') : (theme ? '#ffffff' : '#1a1a1a'),
                              iconColor: showDataHandler ? 'white' : (theme ? '#06b6d4' : '#22d3ee'),
                            },
                          ]}
                        >
                          <MyInputToolbar
                            key="stable-input-toolbar" // Stable key prevents remounting
                            disableEntry={inputDisabled || onboardingInProgress}
                            messageInput
                            removeTools
                            aiInputStyling
                            hasVoiceInput={Platform.OS != 'web'}
                            enableModelSelector
                            enableModeSelector
                            selectedMode={mode}
                            onModeChange={(mode) => {
                              if (mode === 'plan' || mode === 'magic') {
                                showAlert('New AI modes are coming soon!');
                              } else { setMode(mode) }
                            }}
                            onModelPress={handleModelPressRef.current || handleModelPress}
                            customSendIconStyle={{ left: Platform.select({ web: -110 }) }}
                            customSendIcon={(
                              <AnimatedLottieView
                                source={require("@assets/animations/automation_robot.json")}
                                style={{ width: 28 }}
                                autoPlay
                                loop
                              />
                            )}
                            multiline
                            fileDrop={Platform.OS === 'web'}
                            containerStyle={{
                              shadowRadius: 5,
                              shadowOpacity: 0.4,
                              borderRadius: 12,
                              paddingTop: 20,
                              paddingRight: Platform.select({ native: chatMessages.length > 0 ? 50 : 0 }),
                              backgroundColor: "transparent",
                            }}
                            sendMessageCallback={handleQueryRef.current || handleQuery}
                            placeHolder={getInputPlaceholder()}
                            onDataFileProcessed={handleDataFileProcessedRef.current || handleDataFileProcessed}
                            draggedOver={draggedOver}
                            onDragOver={handleDragOverRef.current || handleDragOver}
                            onDragLeave={handleDragLeaveRef.current || handleDragLeave}
                            onDrop={handleDropRef.current || handleDrop}
                            onDragEnter={handleDragOverRef.current || handleDragOver}
                            onMetadataSubmit={() => handleMetadataSubmit(pendingMediaFile, mediaMetadata, dispatch, setMetadataDialogVisible, setPendingMediaFile, setMediaMetadata)}
                            metadataDialogVisible={metadataDialogVisible}
                            setMetadataDialogVisible={setMetadataDialogVisible}
                            pendingMediaFile={pendingMediaFile}
                            setPendingMediaFile={setPendingMediaFile}
                            mediaMetadata={mediaMetadata}
                            setMediaMetadata={setMediaMetadata}
                            messageSetter={(text: string) => {
                              const previousText = inputToolbarTextRef.current || '';
                              inputToolbarTextRef.current = text || '';

                              // If user manually edited (text changed and not from voice input), stop voice recording
                              if (text !== previousText && text !== voiceInputText) {
                                handleManualEditRef.current?.(text);
                              }
                            }}
                            externalText={voiceInputText || undefined}
                          />
                        </InputWrapper>
                      </View>
                    </View>
                  </View>
                );
              }, [
                // Only include dependencies that actually affect rendering
                // Use memoized hasMessages instead of recalculating
                hasMessages,
                // State values that affect UI
                theme,
                inputDisabled,
                onboardingInProgress,
                isRecording,
                mode,
                voiceInputText,
                draggedOver,
                pendingMediaFile,
                mediaMetadata,
                metadataDialogVisible,
                pendingImageAnalysis,
                showHierarchy,
                showCanvas,
                showEditor,
                showDataHandler,
                activeAgents,
                selectedAgent, // Hide toolbar when agent is selected
                chatMessages.length, // Only length, not full array
                // Refs are stable, don't need to be in dependencies
                // Callback functions are in refs, don't need to be in dependencies
              ])}
              renderMessage={props => {
                const { currentMessage } = props;
                // Render System Message with Action Cards (combined response)
                if (currentMessage.user._id !== globalUser && currentMessage.actionCards && Array.isArray(currentMessage.actionCards)) {
                  return (
                    <ActionCardMessage
                      currentMessage={currentMessage}
                      theme={theme}
                      handleActionCardClick={(template, extractedData) =>
                        handleActionCardClick(template, extractedData, currentMessage.sourcePage || 'EasyModeScreen')
                      }
                      styles={styles}
                      showEditor={showEditor}
                      isEditorConnected={isEditorConnected}
                      handleSendToEditor={handleSendToEditor}
                      handleSendSummaryToEditor={handleSendSummaryToEditor}
                      handleSendConversationToEditor={handleSendConversationToEditor}
                    />
                  );
                }

                // Render Data Source Selector (for reports)
                if (currentMessage.showDataSourceSelector && currentMessage.availableDataSources) {
                  return (
                    <View style={styles.systemMessageContainer}>
                      <AnimatedLottieView
                        source={require("@assets/animations/automation_robot.json")}
                        autoPlay
                        loop
                        style={{ width: 28, height: 28, marginRight: 12, alignSelf: "flex-end" }}
                      />
                      <View style={[styles.systemMessageBubble, { backgroundColor: theme ? '#f0f0f0' : '#2a2a2a', width: '90%' }]}>
                        {currentMessage.text && (
                          <View style={{ marginBottom: 16 }}>
                            <ParsedTextWithReferences
                              text={currentMessage.text}
                              textStyle={{ fontSize: 16, fontWeight: '600', color: theme ? '#1f2937' : '#f9fafb' }}
                              theme={theme}
                            />
                          </View>
                        )}
                        <DataSourceSelector
                          dataSources={currentMessage.availableDataSources}
                          selectedIds={selectedDataSources}
                          theme={theme}
                          onSelectionChange={setSelectedDataSources}
                          onConfirm={handleDataSourceSelection}
                        />
                      </View>
                    </View>
                  );
                }

                // Render Form Message (both profile and onboarding forms, integration suggestions, and model selection)
                if (currentMessage.formSchema || currentMessage.showOnboardingForm || currentMessage.showProfileForm ||
                  (currentMessage.suggestedIntegrations && Array.isArray(currentMessage.suggestedIntegrations) && currentMessage.suggestedIntegrations.length > 0) ||
                  (currentMessage.suggestedModels && Array.isArray(currentMessage.suggestedModels) && currentMessage.suggestedModels.length > 0)) {
                  // For non-onboarding forms, onboardingStage and handleFormSkip may not exist
                  const isActiveForm = currentMessage.formSchema && !currentMessage.formSubmitted;
                  return (
                    <FormMessage
                      ref={isActiveForm ? activeFormMessageRef : null}
                      currentMessage={currentMessage}
                      theme={theme}
                      onboardingStage={onboardingStage}
                      onSubmit={handleFormSubmit}
                      onSkip={() => { handleFormSkip(currentMessage) }}
                      onModelSelection={async (selectedModels) => {
                        await handleModelSelection(selectedModels, {
                          dispatch,
                          setChatMessages,
                          setOnboardingStage,
                          fetchIntegrationSuggestions,
                          globalUser,
                          userData,
                        });
                      }}
                      onModelSkip={async () => {
                        await handleModelSelectionSkip({
                          dispatch,
                          setChatMessages,
                          setOnboardingStage,
                          fetchIntegrationSuggestions,
                          globalUser,
                          userData,
                        });
                      }}
                      styles={styles}
                      initialValues={currentMessage.showProfileForm ? profileInformation : {}}
                      delayFormLoad={currentMessage.showProfileForm ? 4 : undefined}
                      hideButtons={isActiveForm}
                    />
                  );
                }

                // Render Dashboard Builder
                if (currentMessage.showDashboardBuilder && currentMessage.dashboardConfig) {
                  return (
                    <View style={styles.systemMessageContainer}>
                      <AnimatedLottieView
                        source={require("@assets/animations/automation_robot.json")}
                        autoPlay
                        loop
                        style={{ width: 28, height: 28, marginRight: 12, alignSelf: "flex-end" }}
                      />
                      <View style={[styles.systemMessageBubble, { backgroundColor: theme ? '#f0f0f0' : '#2a2a2a', width: '95%' }]}>
                        {currentMessage.text && (
                          <View style={{ marginBottom: 16 }}>
                            <ParsedTextWithReferences
                              text={currentMessage.text}
                              textStyle={{ fontSize: 16, fontWeight: '600', color: theme ? '#1f2937' : '#f9fafb' }}
                              theme={theme}
                            />
                          </View>
                        )}
                        <DashboardBuilder
                          dashboardConfig={currentMessage.dashboardConfig}
                          theme={theme}
                          onSave={(dashboardId) => {
                            console.log('✅ Dashboard saved:', dashboardId);
                            // Invalidate React Query cache so ReportDashboardScreen and other screens see the new dashboard
                            queryClient.invalidateQueries({ queryKey: ['userDashboards'] });
                            // Optionally navigate to the dashboard
                          }}
                        />
                      </View>
                    </View>
                  );
                }

                // Render Question Message (Plan Mode)
                if (currentMessage.planQuestions && Array.isArray(currentMessage.planQuestions) && currentMessage.planQuestions.length > 0 && !currentMessage.questionsAnswered) {
                  return (
                    <QuestionMessage
                      currentMessage={currentMessage}
                      theme={theme}
                      onSubmit={(answers) => handleQuestionSubmit(answers, currentMessage._id)}
                      styles={styles}
                    />
                  );
                }

                // Retryable Error Message (with retry button)
                if (currentMessage.isRetryable && currentMessage.retryQuery) {
                  return (
                    <Animatable.View
                      useNativeDriver
                      style={styles.systemMessageContainer}
                      animation={'bounceIn'}
                    >
                      <AnimatedLottieView
                        source={require("@assets/animations/automation_robot.json")}
                        autoPlay
                        loop
                        style={{ width: 28, height: 28, marginRight: 12, alignSelf: "flex-end" }}
                      />
                      <View style={[styles.systemMessageBubble, { backgroundColor: theme ? '#f0f0f0' : '#2a2a2a' }]}>
                        <SafeMarkDown
                          key={currentMessage.streamHash}
                          content={typeof currentMessage.text === 'string'
                            ? currentMessage.text
                            : (currentMessage.text ? JSON.stringify(currentMessage.text) : 'Error: Invalid message text')
                          }
                        />
                        {/* Retry Button */}
                        <Pressable
                          onPress={() => handleRetryQuery(currentMessage._id, currentMessage.retryQuery)}
                          disabled={inputDisabled}
                          style={{
                            marginTop: 12,
                            paddingVertical: 10,
                            paddingHorizontal: 16,
                            backgroundColor: inputDisabled
                              ? (theme ? '#9ca3af' : '#4b5563')
                              : (theme ? '#3b82f6' : '#2563eb'),
                            borderRadius: 8,
                            alignItems: 'center',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            gap: 8,
                            opacity: inputDisabled ? 0.6 : 1,
                          }}
                        >
                          <AntDesign name="reload" size={16} color="white" />
                          <Text style={{ color: 'white', fontSize: 14, fontWeight: '600' }}>
                            {inputDisabled ? 'Retrying...' : 'Retry'}
                          </Text>
                        </Pressable>
                      </View>
                    </Animatable.View>
                  );
                }

                // Default System Message (without action cards)
                if (currentMessage.user._id !== globalUser && (!currentMessage.actionCards || !Array.isArray(currentMessage.actionCards))) {
                  return (
                    <Animatable.View
                      useNativeDriver
                      style={styles.systemMessageContainer}
                      animation={'bounceIn'}
                    >
                      <AnimatedLottieView
                        source={require("@assets/animations/automation_robot.json")}
                        autoPlay
                        loop
                        style={{ width: 28, height: 28, marginRight: 12, alignSelf: "flex-end" }}
                      />
                      <View style={[styles.systemMessageBubble, { backgroundColor: theme ? '#f0f0f0' : '#2a2a2a' }]}>
                        <SafeMarkDown
                          key={currentMessage.streamHash}
                          content={typeof currentMessage.text === 'string'
                            ? currentMessage.text
                            : (currentMessage.text ? JSON.stringify(currentMessage.text) : 'Error: Invalid message text')
                          }
                        />
                        {/* Retry button for model creation errors */}
                        {currentMessage.canRetry && currentMessage.retryFunction && (
                          <Pressable
                            onPress={() => currentMessage.retryFunction?.()}
                            style={{
                              marginTop: 12,
                              paddingVertical: 10,
                              paddingHorizontal: 16,
                              backgroundColor: theme ? '#3b82f6' : '#2563eb',
                              borderRadius: 8,
                              alignItems: 'center',
                              flexDirection: 'row',
                              justifyContent: 'center',
                              gap: 8,
                            }}
                          >
                            <AntDesign name="reload" size={16} color="white" />
                            <Text style={{ color: 'white', fontSize: 14, fontWeight: '600' }}>
                              Retry
                            </Text>
                          </Pressable>
                        )}
                        {/* Editor Action Buttons - Show when editor is available */}
                        {(showEditor || isEditorConnected) &&
                          !currentMessage.isDisclaimer &&
                          currentMessage.text &&
                          typeof currentMessage.text === 'string' &&
                          currentMessage.text.trim().length > 0 &&
                          handleSendToEditor && (
                            <View style={{
                              flexDirection: 'row',
                              gap: 8,
                              marginTop: 12,
                              paddingTop: 12,
                              borderTopWidth: 1,
                              borderTopColor: theme ? '#e5e7eb' : '#374151'
                            }}>
                              <Pressable
                                onPress={() => handleSendToEditor(currentMessage.text, 'html')}
                                style={{
                                  flex: 1,
                                  backgroundColor: theme ? '#7c3aed' : '#6d28d9',
                                  paddingVertical: 8,
                                  paddingHorizontal: 12,
                                  borderRadius: 6,
                                  alignItems: 'center',
                                  flexDirection: 'row',
                                  justifyContent: 'center',
                                  gap: 6,
                                }}
                              >
                                <AntDesign name="file-text" size={14} color="white" />
                                <Text style={{ color: 'white', fontSize: 12, fontWeight: '600' }}>
                                  Send to Editor
                                </Text>
                              </Pressable>
                              {handleSendSummaryToEditor && (
                                <Pressable
                                  onPress={handleSendSummaryToEditor}
                                  style={{
                                    flex: 1,
                                    backgroundColor: theme ? '#10b981' : '#059669',
                                    paddingVertical: 8,
                                    paddingHorizontal: 12,
                                    borderRadius: 6,
                                    alignItems: 'center',
                                    flexDirection: 'row',
                                    justifyContent: 'center',
                                    gap: 6,
                                  }}
                                >
                                  <AntDesign name="file-text" size={14} color="white" />
                                  <Text style={{ color: 'white', fontSize: 12, fontWeight: '600' }}>
                                    Summary
                                  </Text>
                                </Pressable>
                              )}
                              {handleSendConversationToEditor && (
                                <Pressable
                                  onPress={handleSendConversationToEditor}
                                  style={{
                                    flex: 1,
                                    backgroundColor: theme ? '#3b82f6' : '#2563eb',
                                    paddingVertical: 8,
                                    paddingHorizontal: 12,
                                    borderRadius: 6,
                                    alignItems: 'center',
                                    flexDirection: 'row',
                                    justifyContent: 'center',
                                    gap: 6,
                                  }}
                                >
                                  <AntDesign name="message" size={14} color="white" />
                                  <Text style={{ color: 'white', fontSize: 12, fontWeight: '600' }}>
                                    All Messages
                                  </Text>
                                </Pressable>
                              )}
                            </View>
                          )}
                      </View>
                    </Animatable.View>
                  );
                }

                // Default User Message
                const avatarUri = currentMessage.user?.avatar;
                const hasValidAvatar = avatarUri && typeof avatarUri === 'string' && avatarUri.trim().length > 0;

                return (
                  <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginBottom: 12, marginRight: 16 }}>
                    <View style={{
                      backgroundColor: '#3b82f6',
                      padding: 12,
                      borderRadius: 16,
                      borderBottomRightRadius: 4,
                      maxWidth: '80%'
                    }}>
                      <ParsedTextWithReferences
                        text={currentMessage.text || ''}
                        textStyle={{ color: 'white' }}
                        theme={theme}
                      />
                    </View>
                    {hasValidAvatar ? (
                      <Image
                        source={{ uri: avatarUri }}
                        style={{ height: 32, width: 32, borderRadius: 16, marginLeft: 8, alignSelf: 'flex-end' }}
                      />
                    ) : (
                      <View style={{ height: 32, width: 32, borderRadius: 16, marginLeft: 8, alignSelf: 'flex-end', backgroundColor: '#9ca3af', justifyContent: 'center', alignItems: 'center' }}>
                        <Text style={{ color: 'white', fontSize: 14, fontWeight: '600' }}>
                          {currentMessage.user?.name?.[0]?.toUpperCase() || 'U'}
                        </Text>
                      </View>
                    )}
                  </View>
                );
              }}
              user={{ _id: globalUser, avatar: userAvatar?.img }}
              renderAvatar={null}
            />
          </View>

          {/* Suggested Actions (Discovered via Context) and Action Chips (Detected Actions) */}
          {(suggestedActions.length > 0 || pendingActions.length > 0) && (
            <View style={{
              bottom: Platform.select({ web: -25, native: 50 }),
              left: Platform.select({ web: "10%", native: undefined }),
              zIndex: 10,
              paddingHorizontal: 20,
              paddingVertical: 8,
              position: Platform.select({ web: 'absolute' }),
              alignSelf: 'flex-start',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              backgroundColor: "transparent"
            }}>
              <XStack gap={10} paddingHorizontal={'5%'} justifyContent="flex-start" flexWrap="wrap" alignItems="flex-start">
                {/* Suggested Actions (Context-based) */}
                {suggestedActions.length > 0 && (
                  <View style={{ flexShrink: 1 }}>
                    <ActionArray
                      maxButtonWidth={120}
                      forceMinimized={false}
                      wrap={true}
                      actionArray={suggestedActions}
                      activeComponent=""
                    />
                  </View>
                )}
                {/* Action Chips (Detected from AI) */}
                {pendingActions.length > 0 && (
                  <View style={{ flexShrink: 1, maxWidth: '50%' }}>
                    <ActionChips
                      actions={pendingActions}
                      onExecute={(template, data) => handleExecuteAction(template, data, 'EasyModeScreen')}
                      onDismiss={handleDismissAction}
                      onDismissAll={handleDismissAllActions}
                      onFeedback={handleInlineFeedback}
                      disableAbsolutePositioning={true}
                    />
                  </View>
                )}
              </XStack>
            </View>
          )}

          {/* Note: Action cards are now injected into chat messages instead of overlays */}
          {/* Action buttons have been moved to the input bar */}
          {/* Input Area */}

        </YStack>
        {/* Unified Window - Right Side - All components use the same window */}
        <UnifiedWindow
          theme={theme}
          screenWidth={screenWidth}
          widthAnim={windowWidthAnim}
          windowWidth={windowWidth}
          activeComponent={activeWindowComponent}
          isLoading={windowLoading}
          onClose={() => handleToggleWindow(null)}
          hierarchyProps={{
            theme,
            screenWidth,
            hierarchyWidthAnim: windowWidthAnim,
            selectedDataModel,
            hasHierarchyActions,
            userDataForms,
            hierarchyRef,
            isLoading: false,
            isLoaded: true,
            windowWidth: windowWidth,
            onClose: () => handleToggleWindow(null),
            onSelectDataModel: (form) => {
              setSelectedDataModel(form);
            },
            onClearDataModel: () => {
              console.log('🗑️ [EasyModeScreen] Clearing selected data model');
              setSelectedDataModel(null);
            },
            onStartDesigning: handleQuery
          }}
          canvasProps={{
            theme,
            screenWidth,
            canvasWidthAnim: windowWidthAnim,
            canvasRef: canvas.canvasRef,
            canvasLayers,
            selectedCanvasLayerId,
            showCanvas: true,
            isLoading: false,
            isLoaded: true,
            windowWidth: calculateWindowWidth(screenWidth),
            onClose: () => handleToggleWindow(null),
            onLayerUpdate: handleCanvasLayerUpdate,
            onLayerAdd: handleCanvasLayerAdd,
            onLayerRemove: handleCanvasLayerRemove,
            onLayerSelect: setSelectedCanvasLayerId,
            onExport: handleCanvasExport,
            onSaveToMedia: handleCanvasSaveToMedia,
            onAIEnhance: handleCanvasAIEnhance,
            onAIGenerate: handleCanvasAIGenerate,
            onAddImage: () => setShowMediaSelector(true),
            onMoveLayerUp: (layerId) => {
              if (canvas.canvasRef.current) {
                canvas.canvasRef.current.moveLayerUp(layerId);
              }
            },
            onMoveLayerDown: (layerId) => {
              if (canvas.canvasRef.current) {
                canvas.canvasRef.current.moveLayerDown(layerId);
              }
            },
            onDeleteLayer: handleCanvasLayerRemove,
            onDuplicateLayer: (layerId) => {
              if (canvas.canvasRef.current) {
                canvas.canvasRef.current.duplicateLayer(layerId);
              }
            },
            onLockLayer: (layerId, locked) => {
              handleCanvasLayerUpdate(layerId, { locked });
            },
            onUpdateLayer: handleCanvasLayerUpdate
          }}
          editorProps={{
            theme,
            screenWidth,
            editorWidthAnim: windowWidthAnim,
            editorRef: editorContextHook.editorContext?.ref || { current: null },
            editorContent: editorContextHook.getContent() || '',
            isLoading: false,
            isLoaded: true,
            windowWidth: calculateWindowWidth(screenWidth),
            onClose: () => handleToggleWindow(null),
            onSave: (content: string) => handleEditorSave(content)
          }}
          navigation={navigation}
          dataHandlerProps={{
            theme,
            screenWidth,
            dataHandlerWidthAnim: windowWidthAnim,
            isLoading: false,
            isLoaded: true,
            windowWidth: calculateWindowWidth(screenWidth),
            onClose: () => handleToggleWindow(null)
          }}
          documentProps={{
            theme,
            screenWidth,
            documentWidthAnim: windowWidthAnim,
            document,
            pendingLayout,
            pendingContent,
            documentCreationStage,
            currentPage: currentDocumentPage,
            selectedElementId: selectedDocumentElementId,
            showDocument: activeWindowComponent === 'document',
            isLoading: documentCreationStage === 'layout_planning' || documentCreationStage === 'content_generation' || documentCreationStage === 'design_refinement',
            isLoaded: documentCreationStage === 'ready' || documentCreationStage === 'idle',
            windowWidth: calculateWindowWidth(screenWidth),
            onClose: () => handleToggleWindow(null),
            onPageChange: setCurrentDocumentPage,
            onElementSelect: setSelectedDocumentElementId,
            onElementUpdate: handleDocumentElementUpdate,
            onElementAdd: handleDocumentElementAdd,
            onElementRemove: handleDocumentElementRemove,
            onDesignChange: handleDocumentDesignChange,
            onOrientationChange: (orientation) => {
              handleDocumentDesignChange(document, { orientation });
            },
            onExport: () => {
              // TODO: Implement document export
              Alert.alert('Export', 'Document export functionality coming soon!');
            },
            onSave: () => {
              // TODO: Implement document save
              Alert.alert('Save', 'Document save functionality coming soon!');
            },
            onLayoutApprove: () => {
              if (pendingLayout) {
                handleDocumentLayoutApproval(pendingLayout, true, '');
              }
            },
            onLayoutReject: () => {
              if (pendingLayout) {
                handleDocumentLayoutApproval(pendingLayout, false, '');
              }
            },
            onContentApprove: () => {
              if (pendingContent) {
                handleDocumentContentApproval(pendingContent);
              }
            },
            onContentReject: () => {
              if (pendingContent) {
                // For now, just show a message - could add feedback input later
                setChatMessages(prevMessages =>
                  GiftedChat.append(prevMessages, [{
                    _id: uniqueId(),
                    text: 'Content rejected. Please provide feedback on what changes you\'d like, or I can regenerate the content.',
                    createdAt: new Date(),
                    system: true,
                    received: true
                  }])
                );
                setDocumentCreationStage('layout_approval');
                setPendingContent(null);
              }
            },
          }}
        />
      </View>

      {/* Action Data Form Modal */}
      <ActionDataForm
        visible={actionRequiringData !== null}
        actionTemplate={actionRequiringData}
        onSubmit={handleDataFormSubmit}
        onCancel={() => setActionRequiringData(null)}
        initialData={extractedFormData}
      />

      {/* Post Execution Feedback Modal */}
      <PostExecutionFeedback
        visible={showPostExecutionFeedback}
        actionDescription={formatActionDescriptionForEasyMode(
          lastExecutedAction?.template?.description || lastExecutedAction?.template?.intent || 'Action',
          lastExecutedAction?.template?.intent
        )}
        onFeedback={handlePostExecutionFeedback}
        onDismiss={() => {
          setShowPostExecutionFeedback(false);
          setLastExecutedAction(null);
        }}
        autoDismissAfter={5000}
      />

      {/* Media Selector Modal */}
      <MediaSelector
        visible={showMediaSelector}
        onClose={() => setShowMediaSelector(false)}
        onSelect={handleMediaSelect}
      />

      {/* Sheet Selector Modal */}
      <SheetSelectorModal
        showSheetSelector={showSheetSelector}
        availableSheets={availableSheets}
        theme={theme}
        onSelectSheet={(sheetName: string) => handleSheetSelection(sheetName)}
        onCancel={() => setShowSheetSelector(false)}
      />

      {/* Agent Builder Modal */}
      <AgentBuilderModal
        ref={agentBuilderModalRef}
        onAgentCreated={() => {
          queryClient.invalidateQueries({ queryKey: ['activeAgents'] });
        }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000,
  },
  header: {
    paddingTop: Platform.OS === 'web' ? 20 : 50,
    paddingBottom: 16,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    ...(Platform.OS === 'web' ? {
      position: 'sticky' as any,
      top: 0,
    } : {}),
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '600',
  },
  closeButton: {
    padding: 8,
    borderRadius: 20,
  },
  chatContainer: {
    flex: 1,
    paddingHorizontal: 16,
  },
  systemMessageContainer: {
    flexDirection: "row",
    marginBottom: 12,
    alignItems: "flex-start",
  },
  systemMessageBubble: {
    flex: 1,
    borderRadius: 16,
    padding: 12,
    paddingHorizontal: 16,
    borderBottomLeftRadius: 0,
  },
  inputContainer: {
    paddingVertical: 12,
    borderTopWidth: 1,
    ...(Platform.OS === 'web' ? {
      position: 'absolute' as any,
      bottom: 0,
    } : {}),
  },
});

export default EasyModeScreen;